<template>
  <div class="addFormMainTab">
    <div class="addFormMainTab__data" @click.capture="guardReadOnlyFields">
      <div class="addFormMainTab__data--left">

        <!-- Parent Task -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
          <div class="item-container">
            <ad_filters_icons
              :icon="'arrow_top_bold'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0">Parent Task</p>
          </div>
          <div
            v-if="!parentTaskMenu"
            class="mb-0 item-input"
            :class="{
              'hover': isHovered.includes('editableInput-taskName'),
              'item-input--empty': !parentTaskLabel,
              'item-input--fullfield': parentTaskLabel,
            }"
            style="position: relative;"
            @mouseenter="isHoveredOn('editableInput-taskName')"
            @mouseleave="isHoveredOff('editableInput-taskName')"
            @click="openParentTaskPicker"
          >
            <p class="item-input--p mb-0">
              {{ parentTaskLabel || 'No Name' }}
            </p>
            <span
              @click.stop.prevent="clearParentTask"
              class="item-input--clear"
              v-if="isHovered.includes('editableInput-taskName') && parentTaskLabel"
            >
              <pmdashboard_table_icons
                :icon="'pmdashboard_clear_text_field'"
                class="pmdashboard__clear-text d-block"
              />
            </span>
          </div>
          <!-- A combobox rather than an autocomplete: the list is the existing
          Cue Task IDs, but a name typed straight in is still accepted, the way
          this field has always worked. Filtering happens on the server, so the
          local filter is turned off. -->
          <v-combobox
            v-else
            ref="parentTaskCombobox"
            class="mt-0 pt-0 empty-p-field parentTask-select"
            :items="taskOptions"
            :search-input.sync="taskSearch"
            :loading="taskSearchLoading"
            :item-text="parentTaskOptionText"
            :menu-props="{ openOnFocus: true, maxHeight: 300 }"
            item-value="id"
            return-object
            hide-details
            hide-no-data
            no-filter
            autofocus
            placeholder="Search by Cue Task ID"
            @change="onParentTaskSelected"
            @blur="closeParentTaskPickerSoon"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.cueTaskId || item.taskId }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.taskName }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-combobox>
        </div>

        <!-- Project -->
        <div
          class="addFormMainTab__data--left--item d-flex align-center w-100"
          data-task-owned
          :title="taskOwnedHint"
        >
          <div class="item-container" :class="{ 'item-container--nested': showTaskRelation }">
            <!-- <pmdashboard_table_icons
              :icon="'pmdashboard_book--error'"
              class="item-container--icon"
              v-if="mainTabData.relatedProject?.name === 'Empty'"
              /> -->
            <ad_form_icons
              v-if="showTaskRelation"
              :icon="'pmdashboard_arrow_bottom_right'"
              class="item-container--arrow"
              />
            <ad_form_icons
              :icon="'pmdashboard_book'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('relatedProject'),
                'shake': isInvalid('relatedProject') }">Project</p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-relatedProject'),
              'item-input--empty': relatedProjectName === 'Empty',
              'item-input--fullfield': relatedProjectName !== 'Empty'
            }"
            @mouseover="isHoveredOn('editableInput-relatedProject')"
            @mouseleave="isHoveredOff('editableInput-relatedProject')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100">
              <p
                v-if="relatedProjectName === 'Empty' && !relatedProjectMenu"
                class="mb-0 item-input--p"
                id="editableInput-relatedProject"
                :class="{
                  'hover': isHovered.includes('editableInput-relatedProject'),
                  'item-input--empty': relatedProjectName === 'Empty',
                  'item-input--fullfield': relatedProjectName !== 'Empty'
                }"
                @mouseover="isHoveredOn('editableInput-relatedProject')"
                @mouseleave="isHoveredOff('editableInput-relatedProject')"
                @click="relatedProjectMenu = !relatedProjectMenu"
              >
                Empty
              </p>
              <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative"
                v-if="!relatedProjectMenu && relatedProjectName !== 'Empty'"
                @click="relaunchRelatedProject">
                <div class="user-status-name item-input">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-relatedProject">{{ relatedProjectName }}</p>
                  <span v-if="relatedProjectName !== 'Empty' && isHovered.includes('editableInput-relatedProject')"
                    @click="clearRelatedProject()"
                    class="item-input--clear"
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="relatedProjectMenu && relatedProjectName === 'Empty'"
                :items="projectsList"
                ref="relatedProjectAutocomplete" 
                item-value="id"
                item-text="projectName"
                return-object
                hide-no-data
                hide-details
                id="editableInput-relatedProject"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="handleRelatedProjectUpdate(item)">
                      <v-list-item-title>
                        {{ item?.projectName }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Department -->
        <div
          class="addFormMainTab__data--left--item d-flex align-center w-100"
          v-if="showDepartmentField"
          data-task-owned
          :title="taskOwnedHint"
        >
          <div class="item-container" :class="{ 'item-container--nested': showTaskRelation }">
            <ad_form_icons
              v-if="showTaskRelation"
              :icon="'pmdashboard_arrow_bottom_right'"
              class="item-container--arrow"
              />
            <ad_form_icons
              :icon="'hierarchy'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0">Department</p>
          </div>
          <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative">
            <div class="user-status-name">
              <v-menu
                v-model="departmentMenu"
                :close-on-content-click="true"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="user-status-name-text mb-0 ml-0"
                    :class="{
                      'hover': isHovered.includes('editableInput-department'),
                      'item-input--empty': !departmentLabel,
                      'item-input--fullfield': departmentLabel,
                    }"
                    @mouseover="isHoveredOn('editableInput-department')"
                    @mouseout="isHoveredOff('editableInput-department')"
                    @click="departmentMenu = true"
                  >
                    {{ departmentLabel || 'Empty' }}
                  </p>
                </template>
                <v-list class="wtMenuList">
                  <v-list-item
                    v-for="item in departmentOptions"
                    :key="item.id"
                    class="wtMenuList__item"
                    @click="setDepartment(item)"
                  >
                    <v-list-item-title>{{ item.value }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Client Requester -->
        <div
          class="addFormMainTab__data--left--item d-flex align-center w-100"
          v-if="canEditMainFields"
          data-task-owned
          :title="taskOwnedHint"
        >
          <div class="item-container" :class="{ 'item-container--nested': showTaskRelation }">
            <ad_form_icons
              v-if="showTaskRelation"
              :icon="'pmdashboard_arrow_bottom_right'"
              class="item-container--arrow"
              />
            <pmdashboard_table_icons
              :icon="'pmdashboard_account'"
              class="item-container--icon"
              style="width: 24px !important; height: 24px !important;"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('clientRequester'),
                'shake': isInvalid('clientRequester')
              }">
              Client Req.
            </p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-clientRequester'),
              'item-input--empty': assignmentForm.clientRequester.user.firstName === 'Empty',
              'item-input--fullfield': assignmentForm.clientRequester.user.firstName !== 'Empty'
            }"
            @mouseover="isHoveredOn('editableInput-clientRequester')"
            @mouseleave="isHoveredOff('editableInput-clientRequester')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100">
              <p
                v-if="assignmentForm.clientRequester.user.firstName === 'Empty' && !clientRequesterMenu"
                class="mb-0 item-input--p"
                id="editableInput-clientRequester"
                :class="{
                  'hover': isHovered.includes('editableInput-clientRequester'),
                  'item-input--empty': assignmentForm.clientRequester.user.firstName === 'Empty',
                  'item-input--fullfield': assignmentForm.clientRequester.user.firstName !== 'Empty'
                }"
                @mouseover="isHoveredOn('editableInput-clientRequester')"
                @mouseleave="isHoveredOff('editableInput-clientRequester')"
                @click="clientRequesterMenu = !clientRequesterMenu"
              >
                Empty (select project first)
              </p>
              <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative"
                v-if="!clientRequesterMenu && assignmentForm.clientRequester.user.firstName !== 'Empty'"
                @click="relaunchClientRequester">
                <div class="user-status-name item-input">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-clientRequester">{{ assignmentForm.clientRequester.user?.firstName + " " + assignmentForm.clientRequester.user?.lastName }}</p>
                  <span
                    v-if="assignmentForm.clientRequester.user.firstName !== 'Empty' && isHovered.includes('editableInput-clientRequester')"
                    @click="clearClientRequester()"
                    class="item-input--clear"
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="clientRequesterMenu && assignmentForm.clientRequester.user.firstName === 'Empty'"
                :items="clientRequesterList"
                ref="clientRequesterAutocomplete" 
                item-value="id"
                item-text="name"
                return-object
                hide-no-data
                hide-details
                id="editableInput-clientRequester"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="handleClientRequesterUpdate(item)">
                      <v-list-item-title>
                        {{ item.user.firstName + " " + item.user.lastName }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Pay Period -->
        <div
          class="addFormMainTab__data--left--item d-flex align-center w-100"
          data-task-owned
          :title="taskOwnedHint"
        >
          <div class="item-container" :class="{ 'item-container--nested': showTaskRelation }">
            <ad_form_icons
              v-if="showTaskRelation"
              :icon="'pmdashboard_arrow_bottom_right'"
              class="item-container--arrow"
              />
            <v-icon class="item-container--icon" >
              mdi-cash
            </v-icon>
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('payPeriod'),
                'shake': isInvalid('payPeriod')
              }">
              Pay Period
            </p>
          </div>
          <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative">
            <div class="user-status-name">
              <v-menu
                v-model="payPeriodMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="user-status-name-text mb-0 ml-0"
                    :class="{
                      'hover': isHovered.includes('editableInput-payPeriod'),
                      'item-input--empty': !assignmentForm.payPeriod,
                      'item-input--fullfield': assignmentForm.payPeriod,
                    }"
                    @mouseover="isHoveredOn('editableInput-payPeriod')"
                    @mouseout="isHoveredOff('editableInput-payPeriod')"
                    @click="payPeriodMenu = true"
                  >
                    {{ assignmentForm.payPeriod ? formattedPayPeriod(assignmentForm.payPeriod) : 'Empty' }}
                  </p>
                </template>
                
                <v-card>
                  <v-date-picker
                    type="month"
                    v-model="payPeriodField"
                    @input="onPayPeriodPicker"
                  ></v-date-picker>
                </v-card>
                
              </v-menu>
            </div>
          </div>
        </div>

        <!-- Purchase Order -->
         <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
          <div class="item-container">
            <ad_form_icons
              :icon="'adashboard_PO'"
              class="item-container--icon"
              />
            <!-- Not a required field: the legacy form never gated a publish on
            a Purchase Order, so it is never marked. -->
            <p class="item-container--text mb-0">
              Purchase Order
            </p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-purchaseOrder'),
              'item-input--empty': !assignmentForm.purchaseOrder,
              'item-input--fullfield': assignmentForm.purchaseOrder
            }"
            @mouseover="isHoveredOn('editableInput-purchaseOrder')"
            @mouseleave="isHoveredOff('editableInput-purchaseOrder')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100">
              <p
                v-if="!assignmentForm.purchaseOrder && !purchaseOrderMenu"
                class="mb-0 item-input--p"
                id="editableInput-purchaseOrder"
                :class="{
                  'hover': isHovered.includes('editableInput-purchaseOrder'),
                  'item-input--empty': !assignmentForm.purchaseOrder,
                  'item-input--fullfield': assignmentForm.purchaseOrder
                }"
                @mouseover="isHoveredOn('editableInput-purchaseOrder')"
                @mouseleave="isHoveredOff('editableInput-purchaseOrder')"
                @click="relaunchPurchaseOrder"
              >
                Empty (select project first)
              </p>
              <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative"
                v-if="!purchaseOrderMenu && assignmentForm.purchaseOrder"
                @click="relaunchPurchaseOrder">
                <div class="user-status-name item-input">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-purchaseOrder">{{ assignmentForm.purchaseOrder.purchaseOrderNumber }}</p>
                  <span
                    v-if="assignmentForm.purchaseOrder && isHovered.includes('editableInput-purchaseOrder')"
                    @click="clearPOField()"
                    class="item-input--clear"
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="purchaseOrderMenu && !assignmentForm.purchaseOrder"
                :items="assignmentForm.relatedProject.purchaseOrders"
                ref="purchaseOrderAutocomplete" 
                item-value="id"
                item-text="purchaseOrderNumber"
                return-object
                hide-no-data
                hide-details
                id="editableInput-purchaseOrder"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="handleClientPOUpdate(item)">
                      <v-list-item-title>
                        {{ item.purchaseOrderNumber }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Status -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <pmdashboard_table_icons
              :icon="'pmdashboard_status'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('status'),
                'shake': isInvalid('status') }">Status</p>
          </div>
          <div class="mb-0 item-input-p0">
            <v-menu offset-y :close-on-content-click="true">
              <template v-slot:activator="{ on, attrs }">
                <div
                    class="mb-0 task--title bradius-4"
                    :class="{
                      'hover': isHovered.includes('editableInput-taskStatus'),
                      'item-input--empty': !assignmentForm.status,
                      'item-input--fullfield': assignmentForm.status,
                    }"
                    style="position: relative;"
                    @mouseenter="isHoveredOn('editableInput-taskStatus')"
                    @mouseleave="isHoveredOff('editableInput-taskStatus')"
                    @click="startEditing('editableInput-taskStatus')"
                    v-bind="attrs"
                    v-on="on"
                  >
                <v-chip
                  class="ma-0 addFormMainTab__status--chip"
                  :color="statusColor"
                  label
                >
                  {{ assignmentForm.status }}
                </v-chip>
              </div>
              </template>

              <!-- Dropdown Menu with Subheaders -->
              <v-list>
                <!-- INITIATION -->
                <v-subheader>INITIATION</v-subheader>
                <v-list-item
                  v-for="item in statusOptions['initiation']"
                  :key="item.text"
                  @click="updateStatus(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- IN PROGRESS -->
                <v-subheader>IN PROGRESS</v-subheader>
                <v-list-item
                  v-for="item in statusOptions['inProgress']"
                  :key="item.text"
                  @click="updateStatus(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- BLOCKED -->
                <v-subheader>BLOCKED</v-subheader>
                <v-list-item
                  v-for="item in statusOptions['blocked']"
                  :key="item.text"
                  @click="updateStatus(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- PRE-DELIVERY -->
                <v-subheader>PRE-DELIVERY</v-subheader>
                <v-list-item
                  v-for="item in statusOptions['preDelivery']"
                  :key="item.text"
                  @click="updateStatus(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- FINISHED -->
                <v-subheader>FINISHED</v-subheader>
                <v-list-item
                  v-for="item in statusOptions['finished']"
                  :key="item.text"
                  @click="updateStatus(item)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left :color="item.color">{{ item.icon }}</v-icon>
                      {{ item.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <!-- Billed -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
          <div class="item-container">
            <pmdashboard_table_icons
              :icon="'pmdashboard_billed'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0">Billed</p>
          </div>
          <div class="mb-0 item-input-p0">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100"
              :class="{
                'hover': isHovered.includes('editableInput-billed'),
                }"
                @mouseover="isHoveredOn('editableInput-billed')"
                @mouseleave="isHoveredOff('editableInput-billed')"
                @click="toggleIsBilled()">
              <v-checkbox
                class="mt-0 pt-0 ml-1"
                hide-details="true"
                :input-value="assignmentForm.isBilled"
              ></v-checkbox>
            </div>
          </div>
        </div>

        <!-- Project Manager-->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <pmdashboard_table_icons
              :icon="'pmdashboard_account'"
              class="item-container--icon mr-3"
            />
            <p class="item-container--text mb-0">PM</p>
          </div>
          <div class="mb-0 item-input-p0">
            <div class="pa-0 bradius-4 w-100" :class="{
                'hover': isHovered.includes('editableInput-projectManager'),
                'item-input--empty': !assignmentForm.projectManager?.name,
                'item-input--fullfield': assignmentForm.projectManager?.name
              }"
              @mouseover="isHoveredOn('editableInput-projectManager')"
              @mouseleave="isHoveredOff('editableInput-projectManager')">
              <div class="pmdashboard__task--content-left--text mb-0 d-flex align-center">
                <p
                  v-if="!projectManagerMenu && !assignmentForm.projectManager"
                  class="mb-0 item-input--p"
                  id="editableInput-projectManager"
                  :class="{
                    'hover': isHovered.includes('editableInput-projectManager'),
                    'item-input--empty': !assignmentForm.projectManager?.name,
                    'item-input--fullfield': assignmentForm.projectManager?.name
                  }"
                  @mouseover="isHoveredOn('editableInput-projectManager')"
                  @mouseleave="isHoveredOff('editableInput-projectManager')"
                  @click="projectManagerMenu = !projectManagerMenu"
                >
                  Empty
                </p>
                <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center additional-hover p-relative"
                  v-if="!projectManagerMenu && assignmentForm.projectManager"
                  @click="relaunchPMMenu">
                  <div class="user-status-name item-input">
                    <p class="user-status-name-initials mb-0"
                      id="editableInput-projectManager"
                      :style="{ 'background-color': assignmentForm.projectManager?.initials && getColorForInitials(assignmentForm.projectManager?.initials, assignmentForm.projectManager?.name) }">
                      {{ assignmentForm.projectManager?.initials }}
                    </p>
                    <p class="user-status-name-text mb-0 ml-2" id="editableInput-projectManager">{{ assignmentForm.projectManager?.name }}</p>
                    <span
                      v-if="assignmentForm.projectManager && isHovered.includes('editableInput-projectManager')"
                      @click.prevent="updateAssignmentData({ field: 'projectManager', data: null })"
                      class="item-input--clear"
                    >
                      <pmdashboard_table_icons
                        :icon="'pmdashboard_clear_text_field'"
                        class="pmdashboard__clear-text d-block"
                      />
                    </span>
                  </div>
                </div>
                <v-autocomplete
                  v-show="projectManagerMenu && !assignmentForm.projectManager"
                  v-model="projectManagerField"
                  :items="pmList"
                  ref="pmAutocomplete" 
                  item-value="id"
                  item-text="name"
                  return-object
                  @change="handlePMUpdate"
                  hide-no-data
                  hide-details
                  id="editableInput-projectManager"
                  class="mt-0 pt-0 empty-p-field"
                  style="width: 100%;"
                  autofocus
                  :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item class="additional-hover">
                      <v-list-item-content @click="handlePMUpdate(item)" class="py-2 d-flex justify-space-between align-center">
                        <v-list-item-title class="d-flex align-center">
                          <v-chip
                            class="mr-2 dropdown-initials-chip"
                            :color="getColorForInitials(item?.initials, item?.name)"
                            text-color="white"
                          >
                            {{ item?.initials }}
                          </v-chip>
                          {{ item?.name }}
                          <v-icon v-if="item?.name === temporaryUserStorage?.name"
                            class="ml-auto"
                            style="font-size: 20px;
                              font-weight: 300;
                              margin-bottom: 3px;"
                            right>
                            mdi-check
                          </v-icon>
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="addFormMainTab__data--right">

        <!-- Priority -->
         <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
          <div class="item-container">
            <pmdashboard_table_icons
              :icon="'pmdashboard_priority_flag'"
              class="item-container--icon mr-3"
              />
            <p class="item-container--text mb-0">Priority 
              <span style="font-size: 14px; font-weight: 300; font-style: italic">(opt.)</span>
            </p>
          </div>
          <div class="mb-0 item-input-p0">
            <div class="pa-0 bradius-4 w-100">
              <v-menu
                v-model="priorityMenu"
                :close-on-content-click="true"
                transition="scale-transition"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <div 
                    class="mb-0 item-input--p relative"
                    :class="{
                      'hover': isHovered.includes('editableInput-priority'),
                      'item-input--empty': !assignmentForm.priority,
                      'item-input--fullfield': assignmentForm.priority
                    }"
                      @click="priorityMenu = !priorityMenu"
                    @mouseover="isHoveredOn('editableInput-priority')"
                    @mouseleave="isHoveredOff('editableInput-priority')">
                    <p class="mb-0 item-input--p" 
                      v-bind="attrs"
                      v-on="on">
                      <v-icon v-if="assignmentForm.priority === 'High'" left color="#B13A41" style="width:12px; height: 8px;">mdi-menu-up</v-icon>
                      <v-icon v-else-if="assignmentForm.priority === 'Medium'" 
                        left
                        class="custom-icon"
                        color="#CF940A" 
                        style="width:8px; height: 8px; margin-right: 10px; margin-left: 2px;">
                        mdi-drag-horizontal-variant
                      </v-icon>
                      <v-icon v-else-if="assignmentForm.priority === 'Low'" left color="#4466FF" style="width:12px; height: 8px;">mdi-menu-down</v-icon>
                      {{ assignmentForm.priority ? assignmentForm.priority : 'Empty' }}
                    </p>
                    <span
                      v-if="assignmentForm.priority && isHovered.includes('editableInput-priority')"
                      @click="updateAssignmentData({ field: 'priority', data: null })"
                      class="item-input--clear"
                    >
                      <pmdashboard_table_icons
                        :icon="'pmdashboard_clear_text_field'"
                        class="pmdashboard__clear-text d-block"
                      />
                    </span>
                  </div>
                </template>
                <v-list style="max-height: 300px; overflow-y: auto;">
                  <v-list-item @click="handlePriorityUpdate('High')">
                    <v-list-item-title>
                      <v-icon left color="#B13A41" style="width:12px; height: 8px;">mdi-menu-up</v-icon>
                      High
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="handlePriorityUpdate('Medium')">
                    <v-list-item-title>
                      <v-icon
                        left
                        class="custom-icon"
                        color="#CF940A" 
                        style="width:8px; height: 8px; margin-right: 14px; margin-left: -2px;">
                        mdi-drag-horizontal-variant
                      </v-icon>
                      Medium
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="handlePriorityUpdate('Low')">
                    <v-list-item-title>
                      <v-icon left color="#4466FF" style="width:12px; height: 8px;">mdi-menu-down</v-icon>
                      Low
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>
        </div>

        <!-- File Location -->
         <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <pmdashboard_table_icons
              :icon="'pmdashboard_fileLocation'"
              class="item-container--icon mr-3"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('fileLocation'),
                'shake': isInvalid('fileLocation') }">
              File Location
            </p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-fileLocation'),
              'item-input--empty': !assignmentForm.fileLocation,
              'item-input--fullfield': assignmentForm.fileLocation
            }"
            @mouseover="isHoveredOn('editableInput-fileLocation')"
            @mouseleave="isHoveredOff('editableInput-fileLocation')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100"
              @click="relaunchFileLocation">
              <p
                v-if="!assignmentForm.fileLocation && !fileLocationMenu"
                class="mb-0 bradius-4 w-100"
                id="editableInput-fileLocation"
                :class="{
                  'hover': isHovered.includes('editableInput-fileLocation'),
                  'item-input--empty': !assignmentForm.fileLocation,
                  'item-input--fullfield': assignmentForm.fileLocation
                }"
                @mouseover="isHoveredOn('editableInput-fileLocation')"
                @mouseleave="isHoveredOff('editableInput-fileLocation')"
                @click="fileLocationMenu = !fileLocationMenu"
              >
                {{ assignmentForm.fileLocation ? assignmentForm.fileLocation.label : "Empty" }}
              </p>
              <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100 relative"
                v-if="!fileLocationMenu && assignmentForm.fileLocation"
                @click="relaunchFileLocation">
                <div class="mb-0 item-input--p">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-fileLocation">
                    <a target="_blank" :href="fileLocationOptions.find(opt => opt.label === assignmentForm.fileLocation)?.link">{{ fileLocationOptions.find(opt => opt.label === assignmentForm.fileLocation)?.label }}</a>
                  </p>
                  <span
                    v-if="assignmentForm.fileLocation && isHovered.includes('editableInput-fileLocation')"
                    @click="clearFileLocation()"
                    class="item-input--clear"
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="fileLocationMenu && !assignmentForm.fileLocation"
                :items="fileLocationOptions"
                ref="fileLocationAutocomplete"
                item-text="label"
                return-object
                hide-no-data
                hide-details
                id="editableInput-fileLocation"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="handleFileLocationUpdate(item)">
                      <v-list-item-title>
                        {{ item?.label }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Email Subject Line -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
          <div class="item-container">
            <ad_form_icons
              :icon="'email_subject_line'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('emailSubjectLine'),
                'shake': isInvalid('emailSubjectLine') }">Email Subject Line</p>
          </div>
          <div
            v-if="!isEditing.includes('editableInput-emailSubjectLine')"
            class="mb-0 item-input"
            :class="{
              'hover': isHovered.includes('editableInput-emailSubjectLine'),
              'item-input--empty': !assignmentForm.emailSubjectLine,
              'item-input--fullfield': assignmentForm.emailSubjectLine,
            }"
            style="position: relative;"
            @mouseenter="isHoveredOn('editableInput-emailSubjectLine')"
            @mouseleave="isHoveredOff('editableInput-emailSubjectLine')"
            @click="startEditing('editableInput-emailSubjectLine')"
          >
            <p class="item-input--p mb-0">
              {{ assignmentForm.emailSubjectLine ? assignmentForm.emailSubjectLine : 'No Name' }}
            </p>
            <span
              @click.stop.prevent="updateAssignmentData({ field: 'emailSubjectLine', data: '' })"
              class="item-input--clear"
              v-if="isHovered.includes('editableInput-emailSubjectLine') && assignmentForm.emailSubjectLine"
            >
              <pmdashboard_table_icons
                :icon="'pmdashboard_clear_text_field'"
                class="pmdashboard__clear-text d-block"
              />
            </span>
          </div>
          <input
            v-else
            type="text"
            ref="editableInput-emailSubjectLine"
            v-model="emailSubjectLineField"
            @blur="stopEditing('editableInput-emailSubjectLine')"
            @keydown.enter="stopEditing('editableInput-emailSubjectLine')"
            class="hover item-input--empty item-input--field bradius-4"
          />
        </div>

        <!-- Media Type -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <ad_form_icons
              :icon="'media_type'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('mediaType'),
                'shake': isInvalid('mediaType') }">Media Type</p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-mediaType'),
              'item-input--empty': !assignmentForm.mediaType,
              'item-input--fullfield': assignmentForm.mediaType
            }"
            @mouseover="isHoveredOn('editableInput-mediaType')"
            @mouseleave="isHoveredOff('editableInput-mediaType')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100">
              <p
                v-if="!assignmentForm.mediaType && !mediaTypeMenu"
                class="mb-0 item-input--p"
                id="editableInput-mediaType"
                :class="{
                  'hover': isHovered.includes('editableInput-mediaType'),
                  'item-input--empty': !assignmentForm.mediaType,
                  'item-input--fullfield': assignmentForm.mediaType
                }"
                @mouseover="isHoveredOn('editableInput-mediaType')"
                @mouseleave="isHoveredOff('editableInput-mediaType')"
                @click="mediaTypeMenu = !mediaTypeMenu"
              >
                Empty
              </p>
              <div class="addFormMainTab__task--content-left--text w-100 mb-0 d-flex align-center additional-hover relative"
                v-if="!mediaTypeMenu && assignmentForm.mediaType"
                @click="relaunchMediaType">
                <div class="mb-0 item-input--p w-100">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-mediaType">{{ assignmentForm.mediaType }}</p>
                  <span v-if="assignmentForm.mediaType && isHovered.includes('editableInput-mediaType')"
                    @click="updateAssignmentData({ field: 'mediaType', data: null })"
                    class="item-input--clear"
                    style="position: absolute; "
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="mediaTypeMenu && !assignmentForm.mediaType"
                :items="mediaTypeOptions"
                ref="mediaTypeAutocomplete" 
                hide-no-data
                hide-details
                id="editableInput-mediaType"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="setMediaType(item)">
                      <v-list-item-title>
                        {{ item }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Keyword -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <ad_form_icons
              :icon="'keyword'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('keyword'),
                'shake': isInvalid('keyword') }">Keyword</p>
          </div>
          <div class="mb-0 item-input-p0" :class="{
              'hover': isHovered.includes('editableInput-keyword'),
              'item-input--empty': !assignmentForm.keyword,
              'item-input--fullfield': assignmentForm.keyword
            }"
            @mouseover="isHoveredOn('editableInput-keyword')"
            @mouseleave="isHoveredOff('editableInput-keyword')">
            <div class="addFormMainTab__task--content-left--text mb-0 d-flex align-center w-100">
              <p
                v-if="!assignmentForm.keyword && !keywordMenu"
                class="mb-0 item-input--p"
                id="editableInput-keyword"
                :class="{
                  'hover': isHovered.includes('editableInput-keyword'),
                  'item-input--empty': !assignmentForm.keyword,
                  'item-input--fullfield': assignmentForm.keyword
                }"
                @mouseover="isHoveredOn('editableInput-keyword')"
                @mouseleave="isHoveredOff('editableInput-keyword')"
                @click="keywordMenu = !keywordMenu"
              >
                Empty
              </p>
              <div class="addFormMainTab__task--content-left--text w-100 mb-0 d-flex align-center additional-hover relative"
                v-if="!keywordMenu && assignmentForm.keyword"
                @click="relaunchKeyword">
                <div class="mb-0 item-input--p w-100">
                  <p class="user-status-name-text mb-0 ml-2" id="editableInput-keyword">{{ assignmentForm.keyword }}</p>
                  <span v-if="assignmentForm.keyword && isHovered.includes('editableInput-keyword')"
                    @click="updateAssignmentData({ field: 'keyword', data: null })"
                    class="item-input--clear"
                    style="position: absolute; "
                  >
                    <pmdashboard_table_icons
                      :icon="'pmdashboard_clear_text_field'"
                      class="pmdashboard__clear-text d-block"
                    />
                  </span>
                </div>
              </div>
              <v-autocomplete
                v-show="keywordMenu && !assignmentForm.keyword"
                :items="keywordOptions"
                ref="keywordAutocomplete" 
                hide-no-data
                hide-details
                id="editableInput-keyword"
                class="mt-0 pt-0 empty-p-field"
                autofocus
                :menu-props="{ openOnFocus: true, closeOnContentClick: true }"
              >
                <template v-slot:item="{ item }">
                  <v-list-item class="additional-hover">
                    <v-list-item-content @click="setKeyword(item)">
                      <v-list-item-title>
                        {{ item }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
        </div>

        <!-- Genre -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <ad_form_icons
              :icon="'genre'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0">Genre</p>
          </div>
          <div
            v-if="!isEditing.includes('editableInput-genre')"
            class="mb-0 item-input"
            :class="{
              'hover': isHovered.includes('editableInput-genre'),
              'item-input--empty': !assignmentForm.genre,
              'item-input--fullfield': assignmentForm.genre,
            }"
            style="position: relative;"
            @mouseenter="isHoveredOn('editableInput-genre')"
            @mouseleave="isHoveredOff('editableInput-genre')"
            @click="startEditing('editableInput-genre')"
          >
            <p class="item-input--p mb-0">
              {{ assignmentForm.genre ? assignmentForm.genre : 'Empty' }}
            </p>
            <span
              @click.stop.prevent="updateAssignmentData({ field: 'genre', data: '' })"
              class="item-input--clear"
              v-if="isHovered.includes('editableInput-genre') && assignmentForm.genre"
            >
              <pmdashboard_table_icons
                :icon="'pmdashboard_clear_text_field'"
                class="pmdashboard__clear-text d-block"
              />
            </span>
          </div>
          <input
            v-else
            type="text"
            ref="editableInput-genre"
            v-model="genreField"
            @blur="stopEditing('editableInput-genre')"
            @keydown.enter="stopEditing('editableInput-genre')"
            class="hover item-input--empty item-input--field bradius-4"
          />
        </div>

        <!-- Word Count -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <ad_form_icons
              :icon="'word_count'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0"
              :class="{ 'error-color': isInvalid('wordCount'),
                'shake': isInvalid('wordCount') }">Word Count</p>
          </div>
          <div
            v-if="!isEditing.includes('editableInput-wordCount')"
            class="mb-0 item-input"
            :class="{
              'hover': isHovered.includes('editableInput-wordCount'),
              'item-input--empty': !assignmentForm.wordCount,
              'item-input--fullfield': assignmentForm.wordCount,
            }"
            style="position: relative;"
            @mouseenter="isHoveredOn('editableInput-wordCount')"
            @mouseleave="isHoveredOff('editableInput-wordCount')"
            @click="startEditing('editableInput-wordCount')"
          >
            <p class="item-input--p mb-0">
              {{ assignmentForm.wordCount ? assignmentForm.wordCount : '0' }}
            </p>
            <span
              @click.stop.prevent="updateAssignmentData({ field: 'wordCount', data: '' })"
              class="item-input--clear"
              v-if="isHovered.includes('editableInput-wordCount') && assignmentForm.wordCount"
            >
              <pmdashboard_table_icons
                :icon="'pmdashboard_clear_text_field'"
                class="pmdashboard__clear-text d-block"
              />
            </span>
          </div>
          <input
            v-else
            type="text"
            ref="editableInput-wordCount"
            v-model="wordCountField"
            @blur="stopEditing('editableInput-wordCount')"
            @keydown.enter="stopEditing('editableInput-wordCount')"
            class="hover item-input--empty item-input--field bradius-4"
          />
        </div>

        <!-- Character Count -->
        <div class="addFormMainTab__data--left--item d-flex align-center w-100">
          <div class="item-container">
            <ad_form_icons
              :icon="'word_count'"
              class="item-container--icon"
              />
            <p class="item-container--text mb-0">Character Count</p>
          </div>
          <div
            v-if="!isEditing.includes('editableInput-characterCount')"
            class="mb-0 item-input"
            :class="{
              'hover': isHovered.includes('editableInput-characterCount'),
              'item-input--empty': !assignmentForm.characterCount,
              'item-input--fullfield': assignmentForm.characterCount,
            }"
            style="position: relative;"
            @mouseenter="isHoveredOn('editableInput-characterCount')"
            @mouseleave="isHoveredOff('editableInput-characterCount')"
            @click="startEditing('editableInput-characterCount')"
          >
            <p class="item-input--p mb-0">
              {{ assignmentForm.characterCount ? assignmentForm.characterCount : '0' }}
            </p>
            <span
              @click.stop.prevent="updateAssignmentData({ field: 'characterCount', data: '' })"
              class="item-input--clear"
              v-if="isHovered.includes('editableInput-characterCount') && assignmentForm.characterCount"
            >
              <pmdashboard_table_icons
                :icon="'pmdashboard_clear_text_field'"
                class="pmdashboard__clear-text d-block"
              />
            </span>
          </div>
          <input
            v-else
            type="text"
            ref="editableInput-characterCount"
            v-model="characterCountField"
            @blur="stopEditing('editableInput-characterCount')"
            @keydown.enter="stopEditing('editableInput-characterCount')"
            class="hover item-input--empty item-input--field bradius-4"
          />
        </div>
      </div>
      </div>
    <div class="addFormMainTab__steps">
      <div class="addFormMainTab__steps--header">
        <h3 class="addFormMainTab__steps--header--title">Deadlines & Assignees</h3>
        <div class="addFormMainTab__steps--header--timeconverter">
          <div class="steps__timeconverter--dropdown" 
            style="width: 158px; margin-right: 5px;">
            <div class="dropdown-wrapper" :class="{'background-color': toggleTimeValue === 'MyTime'}">
              <span class="dropdown-text" @click="updateTimeValue('MyTime')">{{ myTime.label }}</span>
              <button class="dropdown-arrow" id="myTimePicker" @click="toggleMyTimeDropdown"></button>
              <ul v-if="dropdownMyTimeOpen" class="dropdown-menu">
                <li v-for="(option, index) in timeZoneOptions" 
                    :key="index"
                    @click="selectMyOption(option)">
                  {{ option.label }}
                </li>
              </ul>
            </div>
          </div>
          <div class="steps__timeconverter--dropdown" style="width: 168px;">
            <div class="dropdown-wrapper" :class="{'background-color': toggleTimeValue === 'ClientTime'}">
              <span class="dropdown-text" @click="updateTimeValue('ClientTime')">{{ clientTime.label }}</span>
              <button class="dropdown-arrow" id="clientTimePicker" @click="toggleClentDropdown"></button>
              <ul v-if="dropdownClientTimeOpen" class="dropdown-menu">
                <li v-for="(option, index) in clientTimeZoneOptions" 
                    :key="index" 
                    @click="selectClientOption(option)">
                  {{ option.label }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="addFormMainTab__steps--content" @click.capture="guardReadOnlyFields">
        <div class="steps__fields">
          <div class="steps__fields--left">

            <!-- Request Date and Time -->
            <div class="addFormMainTab__data--left--item d-flex align-center w-100">
              <div class="item-container" style="width: 220px;">
                <pmdashboard_table_icons
                  :icon="'pmdashboard_request_date'"
                  class="item-container--icon"
                  />
                <p class="item-container--text mb-0"
                  :class="{ 'error-color': isInvalid('requestDate'),
                    'shake': isInvalid('requestDate') }">
                  Request Date/Time
                </p>
              </div>
              <div
                class="mb-0 item-input bradius-4 bg-2-state"
                :class="{
                  'item-input--empty': !(assignmentForm.requestDate && assignmentForm.requestTime),
                  'item-input--fullfield': (assignmentForm.requestDate && assignmentForm.requestTime),
                }"
                @mouseenter="isHoveredOn('editableInput-requestDate')"
                @mouseleave="isHoveredOff('editableInput-requestDate')"
              >
                <v-menu
                  v-model="requestDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <p class="item-input--p mb-0" @click="showRequestDateMenu">
                      {{ assignmentForm.requestDate && assignmentForm.requestTime ? formattedRequestDateTime : 'Empty' }}
                    </p>
                  </template>

                  <CustomDateTimePicker
                    :preparedDate="assignmentForm.requestDate"
                    :preparedTime="assignmentForm.requestTime"
                    @onDateSelected="onRequestDateSelected"
                    @onTimeSelected="onRequestTimeSelected($event)"/>

                </v-menu>
                <span
                  v-if="isHovered.includes('editableInput-requestDate') && (assignmentForm.requestDate || assignmentForm.requestTime)"
                  @click.stop.prevent="clearRequestDateTime"
                  class="item-input--clear"
                  title="Clear the Request Date and Time"
                >
                  <pmdashboard_table_icons
                    :icon="'pmdashboard_clear_text_field'"
                    class="pmdashboard__clear-text d-block"
                  />
                </span>
              </div>
            </div>

            <!-- Delivery Date and Time -->
            <div class="addFormMainTab__data--left--item d-flex align-center w-100" v-if="canEditMainFields">
              <div class="item-container" style="width: 220px;">
                <pmdashboard_table_icons
                  :icon="'pmdashboard_deliveryDate'"
                  class="item-container--icon"
                  />
                <p class="item-container--text mb-0"
                  :class="{ 'error-color': isInvalid('deliveryDate'),
                    'shake': isInvalid('deliveryDate') }">
                  Delivery Date/Time
                </p>
              </div>
              <div
                class="mb-0 item-input bradius-4 bg-2-state"
                :class="{
                  'item-input--empty': !(assignmentForm.deliveryDate && assignmentForm.deliveryTime),
                  'item-input--fullfield': (assignmentForm.deliveryDate && assignmentForm.deliveryTime),
                }"
                @mouseenter="isHoveredOn('editableInput-deliveryDate')"
                @mouseleave="isHoveredOff('editableInput-deliveryDate')"
              >
                <v-menu
                  v-model="deliveryDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <p class="item-input--p mb-0" @click="showDeliveryDateMenu">
                      {{ assignmentForm.deliveryDate && assignmentForm.deliveryTime ? formattedDeliveryDateTime : 'Empty' }}
                    </p>
                  </template>

                  <CustomDateTimePicker
                    :preparedDate="assignmentForm.deliveryDate"
                    :preparedTime="assignmentForm.deliveryTime"
                    @onDateSelected="onDeliveryDateSelected"
                    @onTimeSelected="onDeliveryTimeSelected($event)"/>

                </v-menu>
                <span
                  v-if="isHovered.includes('editableInput-deliveryDate') && (assignmentForm.deliveryDate || assignmentForm.deliveryTime)"
                  @click.stop.prevent="clearDeliveryDateTime"
                  class="item-input--clear"
                  title="Clear the Delivery Date and Time"
                >
                  <pmdashboard_table_icons
                    :icon="'pmdashboard_clear_text_field'"
                    class="pmdashboard__clear-text d-block"
                  />
                </span>
              </div>
            </div>
          </div>
          <div class="steps__fields--right" v-if="canEditMainFields">

            <!-- Override Staff Availability -->
            <div class="addFormMainTab__data--left--item d-flex align-center w-100">
              <div class="item-container" style="width: 220px;">
                <ad_form_icons
                  :icon="'clock_fast'"
                  class="item-container--icon"
                  />
                <p class="item-container--text mb-0" style="min-width: 180px;">
                  Override Staff Availability
                </p>
              </div>
              <div class="mb-0 item-input--checkbox">
                <v-checkbox
                  class="mt-0 pt-0"
                  style="margin-left: 8px;"
                  hide-details="true"
                  v-model="overrideStaffAvailabilityField"
                ></v-checkbox>
              </div>
            </div>

            <!-- Override Due Dates -->
            <div class="addFormMainTab__data--left--item d-flex align-center w-100">
              <div class="item-container" style="width: 220px;">
                <ad_form_icons
                  :icon="'calendar_close'"
                  class="item-container--icon"
                  />
                <p class="item-container--text mb-0" style="min-width: 180px;">
                  Override Due Dates
                </p>
              </div>
              <div class="mb-0 item-input--checkbox">
                <v-checkbox
                  class="mt-0 pt-0"
                  style="margin-left: 8px;"
                  hide-details="true"
                  v-model="overrideDueDatesField"
                ></v-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="steps__table">
          <div class="steps__table--left">
            <div class="steps__table--left--header">
              <p class="header--text">Step & Assignee</p>
            </div>
            <div class="steps__table--left--row"
              v-if="isStepVisible('primary')"
              :class="{ 'shake': isInvalid('primary') }">
              <v-autocomplete
                v-if="canEditMainFields"
                :item-text="fullName"
                :items="relatedWriters"
                return-object
                v-model="writer"
                label="Primary"
                hide-details
                flat
                :error="isInvalid('primary')"
              ></v-autocomplete>
              <div v-else class="steps__table--left--row--static">
                <p class="static--label mb-0">Primary</p>
                <p class="static--value mb-0">{{ stepAssigneeName('primary') }}</p>
              </div>
            </div>
            <div class="steps__table--left--row" v-if="isStepVisible('secondary')">
              <v-autocomplete
                v-if="canEditMainFields"
                :item-text="fullName"
                :items="relatedEditors"
                return-object
                v-model="editor"
                label="Secondary"
                hide-details
                flat
              ></v-autocomplete>
              <div v-else class="steps__table--left--row--static">
                <p class="static--label mb-0">Secondary</p>
                <p class="static--value mb-0">{{ stepAssigneeName('secondary') }}</p>
              </div>
            </div>
            <div class="steps__table--left--row" v-if="isStepVisible('qc')">
              <v-autocomplete
                v-if="canEditMainFields"
                :item-text="fullName"
                :items="reviewers"
                return-object
                v-model="reviewer"
                label="QC"
                hide-details
                flat
              ></v-autocomplete>
              <div v-else class="steps__table--left--row--static">
                <p class="static--label mb-0">QC</p>
                <p class="static--value mb-0">{{ stepAssigneeName('qc') }}</p>
              </div>
            </div>
          </div>
          <div class="steps__table--left">
            <div class="steps__table--left--header">
              <p class="header--text">Due Date / Time</p>
            </div>
            <div
              class="steps__table--center--row"
              v-if="isStepVisible('primary')"
              @mouseenter="isHoveredOn('editableInput-primaryDueDate')"
              @mouseleave="isHoveredOff('editableInput-primaryDueDate')"
            >
              <v-menu
                v-model="primaryDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="steps__table--center--row--text"
                    :class="{ 'error-color': isInvalid('primaryDue'),
                      'shake': isInvalid('primaryDue') }"
                    @click="primaryDateMenu = true"
                  >
                    {{
                      assignmentForm.schedule.primaryDueDate && assignmentForm.schedule.primaryDueTime ?
                        formattedAssigneeDateTime(assignmentForm.schedule.primaryDueDate, assignmentForm.schedule.primaryDueTime) :
                        'Select Date'
                    }}
                  </p>
                </template>

                <CustomDateTimePicker
                  :preparedDate="assignmentForm.schedule.primaryDueDate"
                  :preparedTime="assignmentForm.schedule.primaryDueTime"
                  :min="deadlineCalendarBounds.primary.min"
                  :max="deadlineCalendarBounds.primary.max"
                  @onDateSelected="onPrimaryDateSelected"
                  @onTimeSelected="onPrimaryTimeSelected($event)"/>

              </v-menu>
              <span
                v-if="isHovered.includes('editableInput-primaryDueDate') && hasStepDueDateTime('primary')"
                @click.stop.prevent="clearStepDueDateTime('primary')"
                class="item-input--clear"
                title="Clear the Primary due date"
              >
                <pmdashboard_table_icons
                  :icon="'pmdashboard_clear_text_field'"
                  class="pmdashboard__clear-text d-block"
                />
              </span>
            </div>
            <div
              class="steps__table--center--row"
              v-if="isStepVisible('secondary')"
              @mouseenter="isHoveredOn('editableInput-secondaryDueDate')"
              @mouseleave="isHoveredOff('editableInput-secondaryDueDate')"
            >
              <v-menu
                v-model="secondaryDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="steps__table--center--row--text"
                    :class="{ 'error-color': isInvalid('secondaryDue'),
                      'shake': isInvalid('secondaryDue') }"
                    @click="secondaryDateMenu = true"
                  >
                    {{
                      assignmentForm.schedule.secondaryDueDate && assignmentForm.schedule.secondaryDueTime ?
                        formattedAssigneeDateTime(assignmentForm.schedule.secondaryDueDate, assignmentForm.schedule.secondaryDueTime) :
                        'Select Date'
                    }}
                  </p>
                </template>

                <CustomDateTimePicker
                  :preparedDate="assignmentForm.schedule.secondaryDueDate"
                  :preparedTime="assignmentForm.schedule.secondaryDueTime"
                  :min="deadlineCalendarBounds.secondary.min"
                  :max="deadlineCalendarBounds.secondary.max"
                  @onDateSelected="onSecondaryDateSelected"
                  @onTimeSelected="onSecondaryTimeSelected($event)"/>

              </v-menu>
              <span
                v-if="isHovered.includes('editableInput-secondaryDueDate') && hasStepDueDateTime('secondary')"
                @click.stop.prevent="clearStepDueDateTime('secondary')"
                class="item-input--clear"
                title="Clear the Secondary due date"
              >
                <pmdashboard_table_icons
                  :icon="'pmdashboard_clear_text_field'"
                  class="pmdashboard__clear-text d-block"
                />
              </span>
            </div>
            <div
              class="steps__table--center--row"
              v-if="isStepVisible('qc')"
              @mouseenter="isHoveredOn('editableInput-qcDueDate')"
              @mouseleave="isHoveredOff('editableInput-qcDueDate')"
            >
              <v-menu
                v-model="qcDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <p
                    class="steps__table--center--row--text"
                    :class="{ 'error-color': isInvalid('qcDue'),
                      'shake': isInvalid('qcDue') }"
                    @click="qcDateMenu = true"
                  >
                    {{
                      assignmentForm.schedule.qcDueDate && assignmentForm.schedule.qcDueTime ?
                        formattedAssigneeDateTime(assignmentForm.schedule.qcDueDate, assignmentForm.schedule.qcDueTime) :
                        'Select Date'
                    }}
                  </p>
                </template>

                <CustomDateTimePicker
                  :preparedDate="assignmentForm.schedule.qcDueDate"
                  :preparedTime="assignmentForm.schedule.qcDueTime"
                  :min="deadlineCalendarBounds.qc.min"
                  :max="deadlineCalendarBounds.qc.max"
                  @onDateSelected="onQCDateSelected"
                  @onTimeSelected="onQCTimeSelected($event)"/>

              </v-menu>
              <span
                v-if="isHovered.includes('editableInput-qcDueDate') && hasStepDueDateTime('qc')"
                @click.stop.prevent="clearStepDueDateTime('qc')"
                class="item-input--clear"
                title="Clear the QC due date"
              >
                <pmdashboard_table_icons
                  :icon="'pmdashboard_clear_text_field'"
                  class="pmdashboard__clear-text d-block"
                />
              </span>
            </div>
          </div>
          <div
            class="steps__table--left"
            :class="{ 'steps__table--left--single': !canEditMainFields }"
          >
            <div class="steps__table--left--header">
              <p class="header--text">Accepted</p>
            </div>

            <!-- A staff member accepts the assignment, not each step of it —
            one checkmark, the way the assignments table shows it, even when the
            same person happens to hold more than one step. It writes whichever
            of primaryAccepted / secondaryAccepted is theirs, resolved by the
            same primary-then-secondary precedence the table uses. -->
            <div class="steps__table--center--row steps__table--center--row--fill" v-if="!canEditMainFields">
              <ADAcceptedSelector
                v-if="staffAcceptRole"
                data-readonly-allowed
                :role="staffAcceptRoles"
                :item="acceptedItem"
                :value="staffAccepted"
                @refresh-table-expansion="onAcceptedChanged"
              />
              <p v-else class="steps__table--center--row--text">-</p>
            </div>

            <!-- Managers and admins get one per step: the steps can be three
            different people, and each accepts their own. -->
            <template v-else>
              <div class="steps__table--center--row" v-if="isStepVisible('primary')">
                <ADAcceptedSelector
                  v-if="canToggleAccepted('primary')"
                  data-readonly-allowed
                  :role="'primary'"
                  :item="acceptedItem"
                  :value="!!assignmentForm.primaryAccepted"
                  @refresh-table-expansion="onAcceptedChanged"
                />
                <p v-else class="steps__table--center--row--text">
                  {{ assignmentForm.primaryAccepted ? "Yes" : "-" }}
                </p>
              </div>
              <div class="steps__table--center--row" v-if="isStepVisible('secondary')">
                <ADAcceptedSelector
                  v-if="canToggleAccepted('secondary')"
                  data-readonly-allowed
                  :role="'secondary'"
                  :item="acceptedItem"
                  :value="!!assignmentForm.secondaryAccepted"
                  @refresh-table-expansion="onAcceptedChanged"
                />
                <p v-else class="steps__table--center--row--text">
                  {{ assignmentForm.secondaryAccepted ? "Yes" : "-" }}
                </p>
              </div>
              <!-- QC has no accept step: the record carries no qcAccepted field,
              which is why ADAcceptedSelector hides its checkbox for that role. -->
              <div class="steps__table--center--row" v-if="isStepVisible('qc')">
                <p class="steps__table--center--row--text">-</p>
              </div>
            </template>
          </div>
        </div>
        <!-- A time axis, not a step counter. Request Date and Delivery Date pin
        the two ends; the Primary / Secondary / QC deadlines that are set sit
        where they actually fall in between, so the spacing between the dots is
        itself readable as the shape of the schedule. -->
        <div
          class="steps__timeline"
          v-if="canEditMainFields"
          :class="`steps__timeline--${timelineState}`"
        >
          <p class="steps__timeline--header">{{ timelineHeader }}</p>

          <div
            class="steps__timeline--body"
            v-if="timelineState !== 'unset' && timelineState !== 'invalid'"
          >
            <div class="timeline-track" ref="timelineTrack">
              <div class="timeline-track--fill" :style="{ width: timelineFillWidth }"></div>

              <!-- Each dot carries its own tooltip, shown only on hover. Routed
              through v-tooltip (rather than a plain CSS-positioned box) because
              this panel scrolls inside itself (.scrollable-col has overflow-y:
              auto / overflow-x: hidden) — anything absolutely positioned inside
              that clips at its edges, while v-tooltip detaches its content to
              the app root, so it always draws on top uncropped. -->
              <div
                v-for="dot in timelineDots"
                :key="dot.key"
                class="timeline-dot"
                :class="[
                  `timeline-dot--${dot.state}`,
                  { 'timeline-dot--flagged': dot.outOfRange },
                ]"
                :style="{ left: dot.left }"
              >
                <v-tooltip bottom color="#0F62FE" content-class="timeline-tooltip-content">
                  <template v-slot:activator="{ on, attrs }">
                    <span class="circle" v-bind="attrs" v-on="on"></span>
                  </template>
                  <!-- divs, not spans: block-level by default, so the label and
                  the countdown stay on their own lines even before any of the
                  styling below reaches this node. -->
                  <div class="timeline-tooltip--label">{{ dot.label }}</div>
                  <div class="timeline-tooltip--relative">{{ dot.relative }}</div>
                  <div v-if="dot.outOfRange" class="timeline-tooltip--flag">
                    Outside the Request to Delivery window
                  </div>
                </v-tooltip>
              </div>
            </div>
          </div>

          <p v-else class="steps__timeline--empty mb-0">
            <template v-if="timelineState === 'invalid'">
              The Delivery Date has to be after the Request Date for the timeline to be drawn.
            </template>
            <template v-else>
              Set the Request Date and the Delivery Date to see the timeline.
            </template>
          </p>
        </div>
      </div>  
    </div>
    <div class="addFormMainTab__workTypes">
      <div class="addFormMainTab__workTypes--header">
        <h3 class="addFormMainTab__workTypes--header--title"
          :class="{ 'error-color': isInvalid('workTypes') || isInvalid('workTypeQuantity'),
            'shake': isInvalid('workTypes') || isInvalid('workTypeQuantity') }">Work Types &amp; Work Quantity</h3>
        <!-- A plain v-select: it brings its own click-outside, keyboard support
        and type ramp, which the hand-rolled dropdown this replaced did not. -->
        <div class="addFormMainTab__workTypes--header--pricingModel" v-if="canEditMainFields">
          <v-select
            class="workTypes-preset-select mt-0 pt-0"
            :items="pricingPresetOptions"
            :value="activePricingPreset"
            @change="selectPricingPreset"
            :menu-props="{ offsetY: true }"
            hide-details
            dense
            solo
            flat
          >
            <!-- Label rendered inside the selection rather than the prepend
            slot: prepend is laid out on its own baseline with its own margins,
            which is what had the label and the value sitting on different lines
            with a gap between them. -->
            <template v-slot:selection="{ item }">
              <span class="workTypes-preset-select--label">Pricing Preset:</span>
              <span class="workTypes-preset-select--value">{{ item }}</span>
            </template>
          </v-select>
        </div>
      </div>
      <div class="addFormMainTab__workTypes--content">
        <div class="addFormMainTab__workTypes--content--table">
          <div class="workTypes-table--header" :style="workTypesGridStyle">
            <div class="workTypes-table--header--item"
              :class="{ 'error-color': isInvalid('workTypes') }">Work Type</div>
            <div class="workTypes-table--header--item is-num"
              :class="{ 'error-color': isInvalid('workTypeQuantity') }">{{ quantityColumnLabel }}</div>
            <!-- What an assignment costs is an admin concern, the same rule the
            assignments table applies: managers and staff see the work and the
            quantity, not the money. -->
            <div class="workTypes-table--header--item is-num" v-if="showWorkTypeRates">Primary Rate</div>
            <div class="workTypes-table--header--item is-num" v-if="showWorkTypeRates">Secondary Rate</div>
            <div class="workTypes-table--header--item is-num" v-if="showWorkTypeRates">Client Rate</div>
            <!-- Status is driven by where the assignment's hours have got to, not
            by the preset — it appears for Pending Hours / Reviewing Hours exactly
            as it did before presets existed. -->
            <div class="workTypes-table--header--item is-center" v-if="showWorkTypeStatus">Status</div>
            <div class="workTypes-table--header--item is-end"></div>
          </div>

          <div
            class="workTypes-table--row"
            :style="workTypesGridStyle"
            v-for="(workType, i) in sortedWorkTypes"
            :key="workTypeRowKey(workType, i)"
          >
            <!-- Work Type. The name is a link to the price list in a new tab, the
            way it has always been. Changing it is a separate affordance on the
            rest of the cell, so clicking the name never means "edit this".
            Editable under Custom only; a preset prescribes its own rows. -->
            <div
              class="workTypes-table--row--item"
              :class="{ 'workTypes-table--worktype-cell': canEditWorkTypes }"
              @click="beginEditingWorkType(workType)"
            >
              <v-autocomplete
                v-if="editingWorkType === workType"
                class="workTypes-table--worktype-select mt-0 pt-0"
                :items="workTypeReferences"
                :value="workType.customerPriceList"
                item-text="workTypeName"
                return-object
                hide-details
                hide-no-data
                flat
                dense
                autofocus
                placeholder="not selected"
                @change="setWorkTypeReference(workType, $event)"
                @blur="closeWorkTypeEditorSoon"
              ></v-autocomplete>
              <template v-else>
                <a
                  v-if="workType.customerPriceList"
                  class="workTypes-table--worktype-name"
                  @click.stop="
                    checkPrice(
                      workType.customerPriceList?.workTypeCategory,
                      workType.customerPriceList?.workTypeName
                    )
                  "
                >
                  {{ workType.customerPriceList.workTypeName }}
                </a>
                <span v-else class="workTypes-table--worktype-empty">not selected</span>
              </template>
            </div>

            <!-- Quantity. Editable under every preset — it is the one thing a
            preset does not fix — except while logged hours are what define it. -->
            <div class="workTypes-table--row--item is-num">
              <template v-if="quantityFromHours">
                {{ convertToDecimal() }}
                <v-icon
                  v-if="groupedTooltipHours[0]?.status !== 'Not Submitted'"
                  @click="showTooltip = !showTooltip"
                  size="20"
                  style="margin-left: 3.5px;"
                >
                  mdi-information
                </v-icon>
                <v-tooltip
                  v-model="showTooltip"
                  top
                  color="white"
                  content-class="custom-tooltip"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on"></span>
                  </template>
                  <div
                    v-for="(hourItem, idx) in groupedTooltipHours"
                    :key="idx"
                    :class="{ 'mb-2': idx !== groupedTooltipHours.length - 1 }"
                  >
                    <p class="mb-0 tooltip-date">{{ hourItem.dateForInfo }} - {{ hourItem.hours }}h {{ hourItem.minutes }}m</p>
                    <p class="mb-0 tooltip-note" v-if="hourItem.note">{{ hourItem.note }}</p>
                  </div>
                </v-tooltip>
              </template>
              <input
                v-else-if="canEditMainFields"
                class="workTypes-table--quantity-input bradius-4"
                type="number"
                min="0"
                placeholder="0"
                :value="workType.quantity"
                @input="setWorkTypeQuantity(workType, $event.target.value)"
              />
              <span v-else>{{ workType.quantity ?? 0 }}</span>
            </div>

            <!-- Primary Rate -->
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">
              {{ `$${(workType.customerPriceList?.primaryPriceCard ?? 0).toFixed(2)}` }}
            </div>

            <!-- Secondary Rate -->
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">
              {{
                workType.customerPriceList?.secondaryPriceCard !== null
                  ? `$${(workType.customerPriceList?.secondaryPriceCard ?? 0).toFixed(2)}`
                  : "-"
              }}
            </div>

            <!-- Client Rate -->
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">
              {{
                workType.customerPriceList?.clientPriceCard !== null
                  ? `$${(workType.customerPriceList?.clientPriceCard ?? 0).toFixed(2)}`
                  : "-"
              }}
            </div>

            <!-- Status -->
            <div class="workTypes-table--row--item is-center" v-if="showWorkTypeStatus">
              <v-chip
                :class="{ 'text-white': groupedTooltipHours[0]?.status === 'Approved' }"
                class="px-3 d-flex align-center justify-center"
                small
                :color="groupedTooltipHours[0]?.status === 'Approved' ? '#1976D2' : ''"
              >
                {{ groupedTooltipHours[0]?.status }}
              </v-chip>
            </div>

            <!-- Actions. Removing a row is a Custom-only affordance, since a
            preset owns which rows exist; the hours actions are unchanged. -->
            <div class="workTypes-table--row--item is-end">
              <v-menu
                v-if="canEditWorkTypes || showHoursRowActions"
                offset-y
                top
                transition="slide-y-transition"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn text icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list class="wtMenuList">
                  <v-list-item
                    v-if="showHoursRowActions && user.user.userRole !== 'STAFF' && groupedTooltipHours[0]?.status !== 'Approved'"
                    class="wtMenuList__item"
                    @click="showReviewModal"
                  >
                    <v-list-item-title>Review Hours</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="showHoursRowActions && groupedTooltipHours[0]?.status !== 'Approved' && isPrimaryAssignee"
                    class="wtMenuList__item"
                    @click="editTimeResubmission"
                  >
                    <v-list-item-title>Change Hours</v-list-item-title>
                  </v-list-item>
                  <!-- Deletes the submission, not the row: it is what lets a
                  staff member start their time log over. Same action the
                  assignments table has always offered. -->
                  <v-list-item
                    v-if="showHoursRowActions"
                    class="wtMenuList__item"
                    @click="removeSubmission = true"
                  >
                    <v-list-item-title>Delete Work Time</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="canEditWorkTypes"
                    class="wtMenuList__item"
                    @click="removeWorkType(workType)"
                  >
                    <v-list-item-title>Delete Work Type</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <!-- TOTAL. Computed from the rows on screen rather than read off the
          record: quantities are editable here now, so a total that only refreshed
          on save would contradict the table above it. -->
          <div
            class="workTypes-table--row workTypes-table--row--total"
            :style="workTypesGridStyle"
            v-if="sortedWorkTypes.length"
          >
            <div class="workTypes-table--row--item">TOTAL</div>
            <div class="workTypes-table--row--item is-num"></div>
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">{{ formatTotal(workTypesTotal('primaryPriceCard')) }}</div>
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">{{ formatTotal(workTypesTotal('secondaryPriceCard')) }}</div>
            <div class="workTypes-table--row--item is-num" v-if="showWorkTypeRates">{{ formatTotal(workTypesTotal('clientPriceCard')) }}</div>
            <div class="workTypes-table--row--item is-center" v-if="showWorkTypeStatus"></div>
            <div class="workTypes-table--row--item is-end"></div>
          </div>
        </div>
        <div
          class="addFormMainTab__workTypes--content--actions"
          v-if="canEditWorkTypes || canLogWorkTime"
        >
          <!-- Only Custom lets rows be added; a preset defines its own set. -->
          <v-btn
            v-if="canEditWorkTypes"
            class="mr-2"
            outlined
            color="#1976D2"
            @click="addWorkTypeRow"
          >
            Add Work Type
          </v-btn>
          <!-- Same rule the assignments table applies: only the primary, only
          once the assignment is actually waiting on hours and none have been
          submitted yet. Nothing to log against an assignment being created. -->
          <v-btn
            v-if="canLogWorkTime"
            outlined
            color="#1976D2"
            @click="openHoursSubmission"
          >
            Log Work Time
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog
      v-model="hoursSubmissionModalOpen"
      max-width="795"
      persistent
    >
      <ADHoursSubmission
      @assignment-dashboard-table-refresh="$emit('assignment-dashboard-table-refresh')"/>
    </v-dialog>

    <v-dialog v-model="removeSubmission" max-width="600" persistent>
      <div class="remove-submission-modal">
        <div class="remove-submission-modal__content">
          <div class="d-flex align-center remove-submission-modal__content--title">
            <v-icon color="#000">mdi-clock-outline</v-icon>
            <h6 class="ml-4">Delete work time?</h6>
          </div>
          <div class="d-flex align-center remove-submission-modal__total--time">
            <p class="mb-0 remove-submission-modal__total--time-text">Logged Total:</p>
            <p class="mb-1 ml-2 remove-submission-modal__total--time-chip">{{ totalWorkedTime }}</p>
            <template v-if="loggedDateRange">
              <p class="mb-0 ml-2 remove-submission-modal__total--time-text2">for</p>
              <p class="mb-1 ml-2 remove-submission-modal__total--time-chip">{{ loggedDateRange }}</p>
            </template>
          </div>
        </div>
        <div class="d-flex justify-end remove-submission-modal__action">
          <v-btn text :loading="removingSubmission" @click="removeSubmission = false">Cancel</v-btn>
          <v-btn
            depressed
            color="#FF5252"
            :loading="removingSubmission"
            class="white--text ml-2"
            @click="removeSubmissionFromDB"
          >
            Delete
          </v-btn>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script>
import _ from "lodash";
import { mapGetters, mapActions } from "vuex";
import { alphabetColors, managersDesiredColors, ROUTES, DOMAIN, fileLocationDropdownOptions } from "@/utils/constants.js";
import { getOneProject, removeStaffHours, getTasks, newGetTaskById } from "@/utils/newDbUtils.js";
import { parseFormDateTime, officeWallClockNow, toApiDate, staffUserId } from "@/utils/assignmentFormUtils.js";
import {
  CUSTOM_PRESET,
  isCustomPricingPreset,
  pricingPresetOptions,
  defaultPricingPreset,
  pricingPresetQuantityLabel,
  pricingPresetWorkTypeNames,
  inferPricingPreset,
  buildPricingPresetRows,
} from "@/utils/pricingPresets.js";
import CustomDateTimePicker from "@/components/ui/CustomDateTimePicker.vue";
import ADHoursSubmission from "@/components/AssignmentDashboard/ADHoursSubmission.vue";
import ADAcceptedSelector from "@/components/AssignmentDashboard/ADAcceptedSelector.vue";
import pmdashboard_table_icons from "@/assets/icons/pmdashboard_table_icons.vue";
import ad_filters_icons from '@/assets/icons/ad_filters_icons.vue';
import ad_form_icons from '@/assets/icons/ad_form_icons.vue';

// How far the work has actually got, read off the status. Used only for QC: the
// record has no qcAccepted field (ADAcceptedSelector hides the checkbox for that
// role entirely — QC has no accept step of its own), so its due-date dot has
// nothing else to check itself against. Primary and Secondary use their own
// primaryAccepted / secondaryAccepted booleans instead, which is a truer signal.
// The fields a parent task supplies. While one is attached they show its
// values and cannot be edited here — changing them is a change to the task.
const TASK_OWNED_FIELDS = [
  "relatedProject",
  "department",
  "clientRequester",
  "payPeriod",
];

const TIMELINE_STATUS_RANK = {
  'Requested': 0,
  'Assigned': 0,
  'Primary Complete': 1,
  'Secondary Complete': 2,
  'QC Complete': 3,
  'Ready for Delivery': 3,
  'Delivered': 4,
};

// Dot diameter, and the smallest centre-to-centre distance that still reads as
// two dots rather than one blob. Placing dots by real elapsed time means two
// deadlines a few hours apart on a month-long assignment land on top of each
// other, so the drawn positions are nudged apart to at least this much.
const TIMELINE_DOT_SIZE = 19;
const TIMELINE_MIN_GAP = 24;

// Fallback track width for the first render, before the element has been
// measured. Only affects the anti-overlap nudge, never the real proportions.
const TIMELINE_ASSUMED_WIDTH = 948;

export default {
  name: 'ADFormMainTab',

  components: {
    ad_filters_icons,
    pmdashboard_table_icons,
    ad_form_icons,
    CustomDateTimePicker,
    ADHoursSubmission,
    ADAcceptedSelector
  },

  props: {
    projectsList: {
      type: Array,
      required: true,
    },
    staffList: {
      type: Array,
      required: true
    },
    staffHours: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      showTooltip: false,
      // Delete-work-time confirmation.
      removeSubmission: false,
      removingSubmission: false,
      // Drives the timeline's "now" marker. Office wall clock, so it compares
      // like with like against the stored deadlines. Ticked on an interval so the
      // countdown in the header stays honest while the form sits open.
      nowTs: officeWallClockNow(),
      nowTimer: null,
      // The work type row whose picker is open, held by reference — only one
      // row is ever being edited at a time.
      editingWorkType: null,
      // Measured width of the timeline track, in px. Only the anti-overlap nudge
      // needs it — the dots' proportions are pure percentages of the date range.
      timelineWidth: 0,
      timelineResizeObserver: null,
      temporaryUserStorage: null,
      isHovered: [],
      isEditing: [],
      fileLocationOptions: fileLocationDropdownOptions,
      priorityMenu: false,
      departmentMenu: false,
      // Parent Task picker: the fetched options, the text being typed, and the
      // debounce handle for the search behind it.
      parentTaskMenu: false,
      taskOptions: [],
      taskSearch: null,
      taskSearchLoading: false,
      taskSearchTimer: null,
      keywordMenu: false,
      mediaTypeMenu: false,
      fileLocationMenu: false,
      projectManagerMenu: false,
      relatedProjectMenu: false,
      clientRequesterMenu: false,
      purchaseOrderMenu: false,
      payPeriodMenu: false,
      statusOptions: {
        initiation: [
          { text: 'Requested', color: '#7BC4C480', icon: 'mdi-circle' },
        ],
        inProgress: [
          { text: 'Assigned', color: '#8CA9C3', icon: 'mdi-circle' },
          { text: 'Primary Complete', color: '#9FB8F8', icon: 'mdi-circle' },
          { text: 'Secondary Complete', color: '#567DDF', icon: 'mdi-circle' },
          { text: 'QC Complete', color: '#3F599B', icon: 'mdi-circle' },
        ],
        blocked: [
          { text: 'Pending Hours', color: '#F1DE99', icon: 'mdi-circle' },
          { text: 'Reviewing Hours', color: '#EFCD56', icon: 'mdi-circle' },
          { text: 'On Hold', color: '#EBCF6D', icon: 'mdi-circle' },
        ],
        preDelivery: [
          { text: 'Ready for Delivery', color: '#C29DF4', icon: 'mdi-circle' },
        ],
        finished: [
          { text: 'Delivered', color: '#70D9A4', icon: 'mdi-circle' },
          { text: 'Canceled', color: '#FFA1A6', icon: 'mdi-circle' },
        ],
      },
      myTime: {
        label: `My (EDT-04:00)`,
        value: "-04:00",
      },
      clientTime: {
        label: `Client (EDT-04:00)`,
        value: "-04:00",
      },
      defaultTime: "-04:00",
      ifTimeZoneWasTriggered: false,
      toggleTimeValue: 'ClientTime',
      dropdownClientTimeOpen: false,
      dropdownMyTimeOpen: false,
      showDatePicker: false,
      showTimePicker: false,
      requestDateMenu: false,
      deliveryTimePicker: false,
      deliveryDateMenu: false,
      deliveryDatePicker: false,
      primaryDateMenu: false,
      qcDateMenu: false,
      secondaryDateMenu: false,
      timeZones: [
        "PST-08:00",
        "PDT-07:00",
        "CST-06:00",
        "EST-05:00",
        "EDT-04:00",
        "GMT+00:00",
        "CET+01:00",
        "CEST+02:00",
        "EET+02:00",
        "EEST+03:00",
        "GST+04:00",
        "HKT+08:00",
        "KST+09:00",
      ],
      assignees: [
        {type: 'Primary', value: 'Anita Xu', primaryDueDate: '2026-12-13', primaryDueTime: '09:48 AM', accepted: false},
        {type: 'Secondary', value: 'John Doe', secondaryDueDate: '2026-12-19', secondaryDueTime: '09:48 AM', accepted: false},
        {type: 'QC', value: 'Jane Doe', qcDueDate: '2026-12-25', qcDueTime: '09:48 AM', accepted: false},
      ]
    };
  },

  computed: {
    ...mapGetters("assignment", [
      "mediaTypeOptions",
      "keywordOptions",
      "assignmentForm",
      "relatedWriters",
      "relatedEditors",
      "reviewers",
      "hoursSubmissionModalOpen",
      "formValidation",
      "workTypeReferences",
      "departmentOptions"
    ]),
    ...mapGetters("auth", ["isAdmin", "isManager", "user"]),
    groupedTooltipHours() {
      const grouped = [];
      this.staffHours.forEach(item => {
        const date = new Date(item.date[0], item.date[1] - 1, item.date[2]);
        const newDate = date.toLocaleDateString('en-US', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
        grouped.push({
          dateForInfo: newDate,
          date: date,
          hours: item.workedHours,
          minutes: item.workedMinutes,
          note: item.note,
          status: item.status === "APPROVED"
            ? "Approved"
            : item.status === "PENDING"
              ? "Pending"
              : item.status === "REVIEWING_HOURS"
                ? "Reviewing Hours"
              : item.status
        })
      })
      if (
        this.staffHours.length === 0 &&
        (this.assignmentForm.status === "Pending Hours" ||
          this.assignmentForm.status === "Reviewing Hours")
      ) {
        grouped.push({
          dateForInfo: "",
          date: "",
          hours: "",
          minutes: "",
          note: "",
          status: "Not Submitted"
        })
      }

      return grouped;
    },
    // The axis the whole bar is drawn on: Request Date to Delivery Date. Those
    // two are the fixed ends; everything else is placed by where it really falls
    // between them. Null when either end is missing, zero-span when the delivery
    // is not after the request — both are states the bar has to say out loud
    // rather than draw something misleading.
    timelineRange() {
      const start = parseFormDateTime(
        this.assignmentForm.requestDate,
        this.assignmentForm.requestTime
      );
      const end = parseFormDateTime(
        this.assignmentForm.deliveryDate,
        this.assignmentForm.deliveryTime
      );
      if (!start || !end) return null;

      const from = start.getTime();
      const to = end.getTime();
      return { from, to, span: to - from };
    },
    // What the bar as a whole is saying. Drives both the wording of the header
    // and the palette, so the two can never disagree.
    timelineState() {
      const range = this.timelineRange;
      if (!range) return 'unset';
      if (range.span <= 0) return 'invalid';

      const status = this.assignmentForm.status;
      if (status === 'Canceled') return 'canceled';
      if (status === 'Delivered') return 'delivered';

      if (this.nowTs < range.from) return 'upcoming';
      if (this.nowTs > range.to) return 'overdue';
      return 'active';
    },
    // Whether a given milestone's own step is done, independent of where the
    // overall assignment stands. Three colors, three meanings — this decides
    // between the last two:
    //   null  → the milestone has no accept step of its own (Request Date).
    //   true  → accepted / delivered / cancelled.
    //   false → not yet, even though the clock has run out.
    stepDoneChecks() {
      const form = this.assignmentForm;
      const qcRank = TIMELINE_STATUS_RANK[form.status];
      return {
        request: () => null,
        primary: () => Boolean(form.primaryAccepted),
        secondary: () => Boolean(form.secondaryAccepted),
        qc: () => qcRank !== undefined && qcRank >= TIMELINE_STATUS_RANK['QC Complete'],
        delivery: () => form.status === 'Delivered' || form.status === 'Canceled',
      };
    },
    // Calendar bounds for the Primary / Secondary / QC pickers: each has to
    // land on or after the step before it (Secondary on/after Primary, QC
    // on/after Secondary — or Primary, if Secondary isn't set — and all three
    // within Request…Delivery), so an invalid deadline is impossible to pick in
    // the first place instead of being caught after the fact on submit. Bounds
    // are date-only — the day a step is due on stays selectable for the next
    // one too, since same-day-but-later-time is still a valid order and the
    // time picker is what actually settles that.
    // Override Due Dates exists specifically to waive this chain, so the
    // calendar goes back to unrestricted while it's checked.
    deadlineCalendarBounds() {
      if (this.assignmentForm.overrideDueDates) {
        return { primary: {}, secondary: {}, qc: {} };
      }

      const form = this.assignmentForm;
      const schedule = form.schedule || {};
      const requestDate = toApiDate(form.requestDate);
      const deliveryDate = toApiDate(form.deliveryDate);
      const primaryDate = toApiDate(schedule.primaryDueDate);
      const secondaryDate = toApiDate(schedule.secondaryDueDate);
      const qcDate = toApiDate(schedule.qcDueDate);

      // Earliest of whichever dates are set. Plain string compare is safe:
      // toApiDate always returns zero-padded "YYYY-MM-DD".
      const earliest = (...dates) => dates.filter(Boolean).sort()[0] || null;

      // The chain is capped from both directions. Only enforcing the lower
      // bound would still let Primary be pushed past an already-set Secondary,
      // breaking the order from the other side and leaving it for submit to
      // catch — which is the thing this is here to avoid.
      // (If existing data is already inconsistent, a step's min can exceed its
      // max and the calendar will offer nothing; Override Due Dates is the way
      // out of that, same as for every other rule here.)
      return {
        primary: {
          min: requestDate,
          max: earliest(secondaryDate, qcDate, deliveryDate),
        },
        secondary: {
          min: primaryDate || requestDate,
          max: earliest(qcDate, deliveryDate),
        },
        qc: {
          min: secondaryDate || primaryDate || requestDate,
          max: deliveryDate,
        },
      };
    },
    // The milestones that are actually set, each carrying where it sits on the
    // axis (0…1) and how it should read. An unset deadline simply has no dot —
    // that is the whole point of being able to clear one.
    timelinePoints() {
      const range = this.timelineRange;
      if (!range || range.span <= 0) return [];

      const form = this.assignmentForm;
      const schedule = form.schedule || {};
      const doneChecks = this.stepDoneChecks;

      return [
        { key: 'request', label: 'Request', date: form.requestDate, time: form.requestTime },
        { key: 'primary', label: 'Primary', date: schedule.primaryDueDate, time: schedule.primaryDueTime },
        { key: 'secondary', label: 'Secondary', date: schedule.secondaryDueDate, time: schedule.secondaryDueTime },
        { key: 'qc', label: 'QC', date: schedule.qcDueDate, time: schedule.qcDueTime },
        { key: 'delivery', label: 'Delivery', date: form.deliveryDate, time: form.deliveryTime },
      ]
        .map((step) => {
          const at = parseFormDateTime(step.date, step.time);
          if (!at) return null;

          const ts = at.getTime();
          // The calendar itself won't let anyone pick outside Request…Delivery
          // (see deadlineCalendarBounds), so this only fires for legacy data or
          // an explicit Override Due Dates — but it can still happen, so it
          // still gets flagged rather than blending in as if it were normal.
          const outOfRange = ts < range.from || ts > range.to;
          const ratio = Math.min(Math.max((ts - range.from) / range.span, 0), 1);
          const passed = ts <= this.nowTs;
          const done = doneChecks[step.key]();

          // Dark blue: still ahead of us. Pink: the deadline has come and gone
          // and the step has not been accepted. Light blue: passed cleanly —
          // either genuinely accepted, or (Request Date) nothing to accept.
          const state = !passed ? 'upcoming' : done === false ? 'pending' : 'done';

          return {
            ...step,
            at,
            ts,
            ratio,
            outOfRange,
            state,
            formatted: this.formatDateTime(at),
            relative: this.relativeToNow(ts),
          };
        })
        .filter(Boolean)
        // A time axis has to read left to right even when the deadlines are out
        // of order — validation is what flags that, not the drawing.
        .sort((a, b) => a.ratio - b.ratio || a.ts - b.ts);
    },
    // The drawn dots. Same points, with the centres nudged apart so deadlines
    // that fall close together stay two distinguishable dots. The nudge is
    // cosmetic and bounded by TIMELINE_MIN_GAP; the tooltip still tells the truth.
    timelineDots() {
      const points = this.timelinePoints;
      if (!points.length) return [];

      const width = this.timelineWidth || TIMELINE_ASSUMED_WIDTH;
      // Centres live inside the track, half a dot in from each end, so the first
      // and last dots sit flush with the rounded ends instead of hanging over.
      const usable = Math.max(width - TIMELINE_DOT_SIZE, 0);
      const centers = points.map((point) => point.ratio * usable);

      // Forward pass: guarantee a minimum gap, reading left to right. A cluster
      // tied at ratio 0 (deadlines before Request, reachable via Override Due
      // Dates) fans out to the right of it and is fully resolved by this alone.
      for (let i = 1; i < centers.length; i++) {
        centers[i] = Math.max(centers[i], centers[i - 1] + TIMELINE_MIN_GAP);
      }

      // The mirror case needs one extra step. A cluster tied at ratio 1
      // (deadlines at or past Delivery) gets fanned right by the pass above and
      // ends up beyond the track. Pin the last dot back to the end first, then
      // let the backward pass fan the rest of that cluster inwards from it.
      // Clamping every dot to `usable` independently instead would stack the
      // whole cluster on the same pixel — it throws away the spacing the
      // forward pass just created — which was the original "dot doesn't show
      // up" bug. Leaving it unclamped is no better: the dot then floats outside
      // the bar entirely.
      centers[centers.length - 1] = Math.min(centers[centers.length - 1], usable);
      for (let i = centers.length - 2; i >= 0; i--) {
        centers[i] = Math.min(centers[i], centers[i + 1] - TIMELINE_MIN_GAP);
      }

      return points.map((point, i) => ({
        ...point,
        left: `${Math.min(Math.max(centers[i], 0), usable) + TIMELINE_DOT_SIZE / 2}px`,
      }));
    },
    // How much of the axis has elapsed. Real time, not step space — that is what
    // makes the fill line up with the dots now that they are placed by date.
    timelineFill() {
      const range = this.timelineRange;
      if (!range || range.span <= 0) return 0;
      if (this.timelineState === 'delivered' || this.timelineState === 'overdue') return 1;
      if (this.timelineState === 'canceled') return 0;
      return Math.min(Math.max((this.nowTs - range.from) / range.span, 0), 1);
    },
    timelineFillWidth() {
      return `${this.timelineFill * 100}%`;
    },
    // "15d 7h to deliver" against the real Delivery Date, and the equivalent for
    // every other state the bar can be in.
    timelineHeader() {
      const range = this.timelineRange;
      if (!range) {
        if (!parseFormDateTime(this.assignmentForm.deliveryDate, this.assignmentForm.deliveryTime))
          return 'No delivery date set';
        return 'No request date set';
      }

      switch (this.timelineState) {
        case 'invalid':
          return 'Delivery Date is not after the Request Date';
        case 'canceled':
          return 'Canceled';
        case 'delivered':
          return 'Delivered';
        case 'upcoming':
          return `Starts in ${this.spanToNow(range.from)}`;
        case 'overdue':
          return `${this.spanToNow(range.to)} past delivery`;
        default:
          return `${this.spanToNow(range.to)} to deliver`;
      }
    },
    timeZoneOptions() {
      const options = [];
      for (let i = 0; i < this.timeZones.length; i++) {
        const offset = this.timeZones[i].slice(-6);
        options.push({
          label: `My (${this.timeZones[i]})`,
          value: offset,
        });
      }
      return options;
    },
    clientTimeZoneOptions() {
      const options = [];
      for (let i = 0; i < this.timeZones.length; i++) {
        const offset = this.timeZones[i].slice(-6);
        options.push({
          label: `Client (${this.timeZones[i]})`,
          value: offset,
        });
      }
      return options;
    },
    clientRequesterList() {
      if (this.assignmentForm.relatedProject && this.assignmentForm.relatedProject.clientRequesters?.length) {
        const clients = this.assignmentForm.relatedProject.clientRequesters;
        clients.forEach((el) => {
          el.name = `${el.user.firstName} ${el.user.lastName}`
        })
        return clients.sort((a, b) => (a.user?.firstName > b.user?.firstName ? 1 : -1));
      } else {
        return [];
      }
    },
    statusColor() {
      const allOptions = [
        ...this.statusOptions.initiation,
        ...this.statusOptions.inProgress,
        ...this.statusOptions.blocked,
        ...this.statusOptions.preDelivery,
        ...this.statusOptions.finished,
      ];
      const statusOption = allOptions.find(
        (option) => option.text === this.assignmentForm.status
      );
      return statusOption ? statusOption.color : '#000';
    },
    pmList() {
      return this.staffList
        .filter(el => el.user.userRole === "MANAGER")
        .map(el => {
          const firstName = el.user.firstName || "";
          const lastName = el.user.lastName || "";

          const initials =
            (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();

          const name = `${firstName} ${lastName}`.trim();

          return {
            ...el,
            initials,
            name
          };
        });
    },
    formattedRequestDateTime() {
      const { requestDate, requestTime } = this.assignmentForm;
      if (!requestDate || !requestTime) {
        return "Empty";
      }
      const date = this.parseDateTime(requestDate, requestTime);
      if (isNaN(date)) return "Empty";
      return this.formatDateTime(date);
    },
    formattedDeliveryDateTime() {
      const { deliveryDate, deliveryTime } = this.assignmentForm;
      if (!deliveryDate || !deliveryTime) {
        return "Empty";
      }
      const date = this.parseDateTime(deliveryDate, deliveryTime);
      if (isNaN(date)) return "Empty";
      return this.formatDateTime(date);
    },
    payPeriodField: {
      get() {
        return this.assignmentForm.payPeriod;
      },
      set(value) {
        this.updateAssignmentData({ field: "payPeriod", data: value });
      },
    },
    projectManagerField: {
      get() {
        return this.assignmentForm.projectManager;
      },
      set(value) {
        this.updateAssignmentData({ field: "projectManager", data: value });
      },
    },
    emailSubjectLineField: {
      get() {
        return this.assignmentForm.emailSubjectLine;
      },
      set(value) {
        this.updateAssignmentData({ field: "emailSubjectLine", data: value });
      },
    },
    genreField: {
      get() {
        return this.assignmentForm.genre;
      },
      set(value) {
        this.updateAssignmentData({ field: "genre", data: value });
      },
    },
    wordCountField: {
      get() {
        return this.assignmentForm.wordCount;
      },
      set(value) {
        this.updateAssignmentData({ field: "wordCount", data: value });
      },
    },
    characterCountField: {
      get() {
        return this.assignmentForm.characterCount;
      },
      set(value) {
        this.updateAssignmentData({ field: "characterCount", data: value });
      },
    },
    overrideStaffAvailabilityField: {
      get() {
        return this.assignmentForm.overrideStaffAvailability;
      },
      set(value) {
        this.updateAssignmentData({ field: "overrideStaffAvailability", data: value });
      },
    },
    overrideDueDatesField: {
      get() {
        return this.assignmentForm.overrideDueDates;
      },
      set(value) {
        this.updateAssignmentData({ field: "overrideDueDates", data: value });
      },
    },
    writer: {
      get() {
        return this.assignmentForm.primary;
      },
      set(value) {
        this.setAssignee("primary", value);
      },
    },
    editor: {
      get() {
        return this.assignmentForm.secondary;
      },
      set(value) {
        this.setAssignee("secondary", value);
      },
    },
    reviewer: {
      get() {
        return this.assignmentForm.qc;
      },
      set(value) {
        this.setAssignee("qc", value);
      },
    },
    // Which presets this project + request type offers, and which one is in
    // force. The chosen preset is not stored on the assignment, so a saved
    // record falls back to whichever preset its rows were built from.
    pricingPresetOptions() {
      return pricingPresetOptions(this.assignmentForm);
    },
    activePricingPreset() {
      const chosen = this.assignmentForm.pricingModel;
      if (chosen && this.pricingPresetOptions.includes(chosen)) return chosen;

      const inferred = inferPricingPreset(this.assignmentForm);
      if (inferred && this.pricingPresetOptions.includes(inferred)) return inferred;

      return defaultPricingPreset(this.assignmentForm);
    },
    // Rows can only be added or removed under Custom, and only by someone who
    // is allowed to edit the record at all.
    canEditWorkTypes() {
      return this.isCustomPreset && this.canEditMainFields;
    },
    isCustomPreset() {
      return isCustomPricingPreset(this.activePricingPreset);
    },
    quantityColumnLabel() {
      return pricingPresetQuantityLabel(this.activePricingPreset);
    },
    // Re-run whenever the thing a preset depends on changes, so the table can
    // follow the project / request type / geo pair it is priced against.
    pricingPresetContext() {
      const form = this.assignmentForm;
      return [
        form.relatedProject?.id ?? form.relatedProject?.name ?? "",
        form.requestType ?? "",
        form.sourceGeo ?? "",
        form.geo ?? "",
      ].join("|");
    },
    // The context and the rows in one watched value, so the handler can see
    // which of the two changed (see the watcher for why that matters).
    pricingPresetSignature() {
      const rows = (this.assignmentForm.pricing?.workTypes ?? [])
        .map((row) => row?.customerPriceList?.workTypeName ?? "-")
        .join(",");
      return `${this.pricingPresetContext}##${rows}`;
    },
    // Status is an hours concept, not a pricing one: it shows for Pending Hours /
    // Reviewing Hours regardless of which preset is selected.
    showWorkTypeStatus() {
      return this.groupedTooltipHours.length > 0;
    },
    // Logged hours are the source of truth for quantity once they exist — the
    // field stops being editable rather than disagreeing with the submission.
    quantityFromHours() {
      return this.groupedTooltipHours.length > 0;
    },
    // The condition ADTableExpansion uses for its own Log Work Time button, kept
    // identical here. Reviewing Hours counts as well as Pending Hours, and the
    // test is that no hours exist rather than that the first row reads "Not
    // Submitted" — otherwise a submission the manager sent back leaves the
    // primary with no way to log again.
    // Every step this staff member holds that carries an accept flag. One
    // person is often both Primary and Secondary, and their single checkmark
    // has to accept the lot — otherwise the manager, who sees a row per step,
    // reads it as only the Primary having been accepted. QC is not here: the
    // record has no qcAccepted field.
    staffAcceptRoles() {
      if (!this.assignmentForm.id) return [];
      const me = this.user?.user?.id;
      if (!me) return [];
      return ["primary", "secondary"].filter(
        (step) => staffUserId(this.assignmentForm[step]) === me
      );
    },
    staffAcceptRole() {
      return this.staffAcceptRoles[0] ?? null;
    },
    // Ticked only once every step they hold is accepted, so a half-accepted
    // record does not read as done.
    staffAccepted() {
      return (
        this.staffAcceptRoles.length > 0 &&
        this.staffAcceptRoles.every((step) => !!this.assignmentForm[`${step}Accepted`])
      );
    },
    // All ADAcceptedSelector needs once the role is passed explicitly: the
    // record to PATCH. Its email-matching path is never reached from here.
    acceptedItem() {
      return { id: this.assignmentForm.id, index: 0 };
    },
    isPrimaryAssignee() {
      return staffUserId(this.assignmentForm?.primary) === this.user?.user?.id;
    },
    canLogWorkTime() {
      const status = this.assignmentForm?.status;
      return (
        (status === "Pending Hours" || status === "Reviewing Hours") &&
        !this.staffHours.length &&
        this.isPrimaryAssignee
      );
    },
    showHoursRowActions() {
      return (
        (this.assignmentForm?.status === "Pending Hours" ||
          this.assignmentForm?.status === "Reviewing Hours") &&
        this.groupedTooltipHours.length > 0 &&
        this.groupedTooltipHours[0]?.status !== "Approved" &&
        this.groupedTooltipHours[0]?.status !== "Not Submitted"
      );
    },
    totalWorkedTime() {
      if (!this.staffHours.length) return "0h 0m";
      let totalHours = 0;
      let totalMinutes = 0;
      this.staffHours.forEach((item) => {
        totalHours += +item.workedHours || 0;
        totalMinutes += +item.workedMinutes || 0;
      });
      totalHours += Math.floor(totalMinutes / 60);
      totalMinutes = totalMinutes % 60;
      return `${totalHours}h ${totalMinutes}m`;
    },
    // Built from groupedTooltipHours, which has already turned the API's
    // [year, month, day] triples into real Dates — `new Date(triple)` is an
    // Invalid Date, which is why the same range renders blank in the
    // assignments table.
    loggedDateRange() {
      const dates = this.groupedTooltipHours
        .map((item) => item.date)
        .filter((date) => date instanceof Date && !isNaN(date));
      if (!dates.length) return "";
      const format = (date) => this.$moment(date).format("DD MMMM YYYY");
      const from = format(new Date(Math.min(...dates)));
      const to = format(new Date(Math.max(...dates)));
      return from === to ? from : `${from} - ${to}`;
    },
    // What ADHoursSubmission is handed. It reads primary.id and sends it as the
    // staffId the hours belong to — but once the assignee lists have loaded the
    // form holds the project's staff object, whose .id is the staff record, not
    // the user. Approving then PUT /staff-hours/:id with the wrong id and the
    // API rejected it. The table never hit this because its assignmentInfo
    // carries flat user objects.
    hoursSubmissionInfo() {
      const form = this.assignmentForm;
      return {
        ...form,
        primary: form.primary
          ? { ...form.primary, id: staffUserId(form.primary) }
          : form.primary,
      };
    },
    // Rates are admin-only, the rule ADTableExpansion already applies to the
    // same three columns.
    showWorkTypeRates() {
      return this.isAdmin;
    },
    // The track list for the header, every row and the total together, so the
    // three can never disagree. Which columns exist depends on the role and on
    // whether hours have been logged, so it is computed rather than a fixed
    // class. The actions column is an icon, not a share of the table.
    workTypesGridStyle() {
      const columns = this.showWorkTypeRates
        ? ["25fr", "16fr", "18fr", "18fr", "18fr"]
        : ["50fr", "50fr"];
      if (this.showWorkTypeStatus) {
        columns.push(this.showWorkTypeRates ? "15fr" : "30fr");
      }
      columns.push("40px");
      return { gridTemplateColumns: columns.join(" ") };
    },
    // Everything below is the staff view. A staff member opens an assignment to
    // see their own step and log time against it, not to edit the record: the
    // fields are read-only, the ones that are none of their business are gone,
    // and the deadline table shows only the row they are named on.
    canEditMainFields() {
      return this.isAdmin || this.isManager;
    },
    // A real parent task, not just a name typed into the field. Project,
    // Department, Client Requester and Pay Period are the task's values while
    // this holds, which is what earns them the arrow and the indent.
    hasParentTask() {
      return !!(this.assignmentForm.taskDbId || this.assignmentForm.taskId);
    },
    // The arrow and the indent say "this comes from the task above". Staff are
    // not shown the parent task at all, so for them these are ordinary fields.
    taskOwnedHint() {
      return this.showTaskRelation
        ? "Comes from the parent task — edit it there, or clear Parent Task to set it here."
        : null;
    },
    showTaskRelation() {
      return this.canEditMainFields && this.hasParentTask;
    },
    parentTaskLabel() {
      return this.assignmentForm.taskName || this.assignmentForm.taskId || "";
    },
    // Only iTunes FC assignments carry a department.
    showDepartmentField() {
      return this.relatedProjectName === "iTunes FC";
    },
    departmentLabel() {
      const department = this.assignmentForm.department;
      if (!department) return "";
      if (typeof department === "object") return department.value || "";
      const match = (this.departmentOptions || []).find((el) => el.id === department);
      return match ? match.value : "";
    },
    // The project arrives twice: slim, on the assignment payload, and full,
    // from getOneProject. The two do not agree on which key holds the name, and
    // the enrichment call is not available to every role — so the field reads
    // whichever key is actually there rather than rendering blank.
    relatedProjectName() {
      const project = this.assignmentForm.relatedProject;
      return project?.name ?? project?.projectName ?? "Empty";
    },
    sortedWorkTypes() {
      const workTypes = this.assignmentForm.pricing?.workTypes ?? [];
      if (
        this.assignmentForm.relatedProject?.name === "iTunes FC" &&
        this.assignmentForm.requestType === "Copywriting"
      ) {
        const getIndex = (wt) => {
          if (
            wt === "CWR_500-60-1100" ||
            wt === "CWR_2500-00-3800" ||
            wt === "CWR_2500-00-4500" ||
            wt === "CWR_3500-00-4500" ||
            wt === "CWR_1000-00-00"
          )
            return 0;
          else if (wt === "CWR_1000-120-1500") return 1;
          else if (wt === "CWR_1500-180-2800") return 2;
          else if (
            wt === "CWR_1000-380-3800" ||
            wt === "CWR_1500-380-3800" ||
            wt === "CWR_2500-300-3800" ||
            wt === "CWR_2000-500-3800"
          )
            return 3;
          else if (wt === "CWR_5000-500-7000") return 4;
          else if (wt === "CWR_300-100-500" || wt === "CWR_500-00-500")
            return 5;
          else return 6;
        };
        // Sort a shallow copy of the array rather than mapping to new objects:
        // the inline editors identify the row they are changing by reference,
        // so handing the template clones would make every edit here a no-op.
        return [...workTypes].sort(
          (a, b) =>
            getIndex(a.customerPriceList?.workTypeName) -
            getIndex(b.customerPriceList?.workTypeName)
        );
      }
      return workTypes;
    }
  },

  watch: {
    // The track is only in the DOM once there is a range to draw, so it has to be
    // measured and observed again whenever it comes back after being unset.
    timelineState() {
      this.$nextTick(this.observeTimeline);
    },
    // Two different things move the preset, and they need opposite treatment:
    // a record arriving (its saved rows decide the preset) and the user changing
    // the project or request type (the new default decides, and the old rows go).
    //
    // They are told apart by whether the rows moved along with the context. A
    // load replaces both at once; editing the request type changes the context
    // and leaves the table as it was.
    taskSearch(value) {
      if (!this.parentTaskMenu) return;
      clearTimeout(this.taskSearchTimer);
      this.taskSearchTimer = setTimeout(() => {
        this.searchTasks(value);
      }, 300);
    },
    pricingPresetSignature: {
      immediate: true,
      handler(next, previous) {
        if (previous === undefined) {
          this.reconcilePricingPreset();
          return;
        }

        const [nextContext, nextRows] = next.split("##");
        const [previousContext, previousRows] = previous.split("##");

        // Only the rows moved: the user is editing the table itself. Leave it be.
        if (nextContext === previousContext) return;

        // Both moved: a record was loaded. Read the preset back out of its rows.
        if (nextRows !== previousRows) {
          this.reconcilePricingPreset();
          return;
        }

        // The context moved on its own: a deliberate change of project or
        // request type, so the new default applies.
        this.applyDefaultPricingPreset();
      },
    },
    // The price list arrives from its own request, so preset rows built before
    // it landed have no reference attached yet and need filling in once it does.
    workTypeReferences() {
      this.reconcilePricingPreset();
    },
  },

  methods: {
    ...mapActions("assignment", [
      "updateAssignmentData",
      "getStaffByGeo",
      "setHoursSubmissionModalOpen",
      "setStaffHoursData",
      "openReviewModal"
    ]),
    ...mapActions("flashMessage", ["handleFlash"]),
    // Staff open this form to read it and to log time against their own step.
    // Rather than gating seventeen field editors one at a time, the click is
    // stopped in the capture phase before it can reach any of them — links are
    // let through, so File Location still opens.
    guardReadOnlyFields(event) {
      const target = event.target;
      const closest =
        target && typeof target.closest === "function"
          ? (selector) => target.closest(selector)
          : () => null;
      // Links still open, and anything explicitly marked stays live — the
      // accept checkbox is a staff member's own action on their own step, not
      // an edit of the record.
      if (closest("a") || closest("[data-readonly-allowed]")) return;
      // Staff read the whole form. Everyone else can still be blocked from the
      // four fields a parent task owns: those values are the task's, and are
      // duplicated onto the assignment rather than chosen here.
      const taskOwned = this.hasParentTask && closest("[data-task-owned]");
      if (!this.canEditMainFields || taskOwned) {
        event.stopPropagation();
        event.preventDefault();
      }
      // Silence is not an explanation. Say why the click did nothing.
      if (taskOwned && this.canEditMainFields) {
        this.handleFlash({
          response: {
            data: {
              message:
                "This comes from the parent task. Edit it on the task, or clear Parent Task to set it here.",
              statusCode: "info",
            },
          },
          show: true,
        });
      }
    },
    // Which deadline rows a staff member sees: the ones they are named on.
    // Managers and admins see the whole schedule.
    // The assignee on a step, for the read-only rendering of these rows.
    stepAssigneeName(step) {
      const person = this.assignmentForm?.[step];
      if (!person) return "-";
      const first = person.user?.firstName ?? person.firstName;
      const last = person.user?.lastName ?? person.lastName;
      const name = [first, last].filter(Boolean).join(" ").trim();
      return name || "-";
    },
    // Only the person named on the step may accept it, which is exactly when
    // the assignments table offers the checkbox. Matched on id rather than
    // email, since that is what the rest of this form compares on.
    canToggleAccepted(step) {
      if (step === "qc") return false;
      if (!this.assignmentForm.id) return false;
      const person = this.assignmentForm?.[step];
      const assigneeId = person?.user?.id ?? person?.id;
      return Boolean(assigneeId) && assigneeId === this.user?.user?.id;
    },
    // ADAcceptedSelector has already written the change; mirror it into the form
    // so the timeline dot for that step repaints straight away, and tell ADForm
    // to re-take its snapshot so the header does not report a phantom edit.
    onAcceptedChanged(index, value, role, resp, roles) {
      (roles && roles.length ? roles : [role]).forEach((step) => {
        this.updateAssignmentData({ field: `${step}Accepted`, data: value });
      });
      this.$emit("assignment-accepted-changed");
    },
    isStepVisible(step) {
      if (this.canEditMainFields) return true;
      const assigneeId = this.assignmentForm?.[step]?.id;
      return Boolean(assigneeId) && assigneeId === this.user?.user?.id;
    },
    // True once a publish has been rejected and this field was one of the
    // reasons. ADForm owns the flag, so nothing here is marked until the user
    // has actually tried to publish — an untouched draft opens clean.
    isInvalid(field) {
      return !!(this.formValidation.intent && this.formValidation.fields[field]);
    },
    // Anchored to UTC to match formatDateTime, which renders with
    // timeZone: "UTC". Building the date locally and reading it back as UTC (what
    // this used to do) shifted every displayed time by the viewer's offset.
    // Returns an Invalid Date when unparseable, so the isNaN() checks on the
    // callers still hold.
    parseDateTime(dateStr, timeStr) {
      return parseFormDateTime(dateStr, timeStr) ?? new Date(NaN);
    },
    formattedAssigneeDateTime(date, time) {
      if (!date || !time) {
        return "No values";
      }
      const dateTime = this.parseDateTime(date, time);
      if (isNaN(dateTime)) return "No values";
      return this.formatDateTime(dateTime);
    },
    fullName(item) {
      return `${item?.user?.firstName} ${item?.user?.lastName}`;
    },
    formatDateTime(date) {
      const datePart = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });

      const timePart = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'UTC',
      });

      return `${datePart} (${timePart})`;
    },
    showRequestDateMenu() {
      this.showTimePicker = false;
      this.requestDateMenu = true;
      this.showDatePicker = true;
    },
    onRequestDateSelected(date) {
      console.log("onRequestDateSelected", date);
      this.updateAssignmentData({ field: "requestDate", data: date });
      this.showDatePicker = false;
      this.showTimePicker = true;
    },
    onRequestTimeSelected(timeAmPm) {
      console.log("onRequestTimeSelected", timeAmPm);
      const [time, modifier] = timeAmPm.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }
      hours = hours.toString().padStart(2, '0');
      this.updateAssignmentData({ field: "requestTime", data: `${hours}:${minutes}:00` });
      this.ifTimeZoneWasTriggered = false;
      this.stopEditing("requestDate");
    },
    showDeliveryDateMenu() {
      this.deliveryTimePicker = false;
      this.deliveryDateMenu = true;
      this.deliveryDatePicker = true;
    },
    onDeliveryDateSelected(date) {
      this.updateAssignmentData({ field: "deliveryDate", data: date });
      this.deliveryDatePicker = false;
      this.deliveryTimePicker = true;
    },
    onDeliveryTimeSelected(timeAmPm) {
      const [time, modifier] = timeAmPm.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }
      hours = hours.toString().padStart(2, '0');
      this.updateAssignmentData({ field: "deliveryTime", data: `${hours}:${minutes}:00` });
      this.ifTimeZoneWasTriggered = false;
      this.stopEditing("deliveryDate");
    },
    onPrimaryDateSelected(date) {
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, primaryDueDate: date} });
    },
    onPrimaryTimeSelected(timeAmPm) {
      const [time, modifier] = timeAmPm.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }
      hours = hours.toString().padStart(2, '0');
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, primaryDueTime: `${hours}:${minutes}:00`} });
    },
    onSecondaryDateSelected(date) {
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, secondaryDueDate: date} });
    },
    onSecondaryTimeSelected(timeAmPm) {
      const [time, modifier] = timeAmPm.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }
      hours = hours.toString().padStart(2, '0');
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, secondaryDueTime: `${hours}:${minutes}:00`} });
    },
    onQCDateSelected(date) {
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, qcDueDate: date} });
    },
    onQCTimeSelected(timeAmPm) {
      const [time, modifier] = timeAmPm.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }
      hours = hours.toString().padStart(2, '0');
      this.updateAssignmentData({ field: "schedule", data: {...this.assignmentForm.schedule, qcDueTime: `${hours}:${minutes}:00`} });
    },
    selectClientOption(option) {
      this.ifTimeZoneWasTriggered = true;
      this.defaultTime = this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value;
      this.clientTime = option;
      this.updateDeliveryTime(`${this.deliveryDate}, ${this.deliveryTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
      this.updateRequestTime(`${this.requestDate}, ${this.requestTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
      this.dropdownClientTimeOpen = false;
    },
    selectMyOption(option) {
      this.ifTimeZoneWasTriggered = true;
      this.defaultTime = this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value;
      this.myTime = option;
      this.updateDeliveryTime(`${this.deliveryDate}, ${this.deliveryTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
      this.updateRequestTime(`${this.requestDate}, ${this.requestTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
      this.dropdownMyTimeOpen = false;
    },
    updateTimeValue(value) {
      if (this.toggleTimeValue === value) return;
      this.toggleTimeValue = value;
      this.defaultTime = value === 'MyTime' ? this.clientTime.value : this.myTime.value;
      this.updateDeliveryTime(`${this.deliveryDate}, ${this.deliveryTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
      this.updateRequestTime(`${this.requestDate}, ${this.requestTime}`, this.defaultTime, this.toggleTimeValue === 'MyTime' ? this.myTime.value : this.clientTime.value);
    },
    updateRequestTime(dateTime, oldOffset, newOffset) {
      const [date, time] = dateTime.split(',').map(s => s.trim());
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = time.split(':').map(Number);
      const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      const oldOffsetMinutes = parseInt(oldOffset) * 60;
      const newOffsetMinutes = parseInt(newOffset) * 60;
      const offsetDifference = newOffsetMinutes - oldOffsetMinutes;
      utcDate.setMinutes(utcDate.getMinutes() + offsetDifference);
      const newYear = utcDate.getUTCFullYear();
      const newMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const newDay = String(utcDate.getUTCDate()).padStart(2, '0');
      const newHours = String(utcDate.getUTCHours()).padStart(2, '0');
      const newMinutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      this.requestTime = `${newHours}:${newMinutes}`;
      this.requestDate = `${newYear}-${newMonth}-${newDay}`
    },
    updateDeliveryTime(dateTime, oldOffset, newOffset) {
      const [date, time] = dateTime.split(',').map(s => s.trim());
      if (date == 'null' || time == 'null') return;
      const [year, month, day] = date.split('-').map(Number);
      const [hours, minutes] = time.split(':').map(Number);
      const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      const oldOffsetMinutes = parseInt(oldOffset) * 60;
      const newOffsetMinutes = parseInt(newOffset) * 60;
      const offsetDifference = newOffsetMinutes - oldOffsetMinutes;
      utcDate.setMinutes(utcDate.getMinutes() + offsetDifference);
      const newYear = utcDate.getUTCFullYear();
      const newMonth = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const newDay = String(utcDate.getUTCDate()).padStart(2, '0');
      const newHours = String(utcDate.getUTCHours()).padStart(2, '0');
      const newMinutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
      this.deliveryTime = `${newHours}:${newMinutes}`;
      this.deliveryDate = `${newYear}-${newMonth}-${newDay}`;
    },
    toggleClentDropdown() {
      this.dropdownClientTimeOpen = !this.dropdownClientTimeOpen;
    },
    toggleMyTimeDropdown() {
      this.dropdownMyTimeOpen = !this.dropdownMyTimeOpen;
    },
    setMediaType(item) {
      this.updateAssignmentData({ field: "mediaType", data: item });
      this.mediaTypeMenu = false;
    },
    setKeyword(item) {
      this.updateAssignmentData({ field: "keyword", data: item });
      this.keywordMenu = false;
    },
    handleFileLocationUpdate(item) {
      this.updateAssignmentData({ field: "fileLocation", data: item.label });
      this.stopEditing("fileLocation");
      this.fileLocationMenu = false;
    },
    startEditing(item) {
      this.isEditing.push(item);
      this.$nextTick(() => {
        this.$refs[item].focus();
      });
    },
    handlePriorityUpdate(item) {
      this.updateAssignmentData({ field: "priority", data: item });
    },
    isHoveredOn(item) {
      // The hover chrome exists to advertise an editor and its clear cross.
      // With the fields read-only there is nothing behind it to advertise.
      if (!this.canEditMainFields) return;
      if (this.hasParentTask && TASK_OWNED_FIELDS.some((f) => item.endsWith(f))) return;
      if (!this.isHovered.includes(item)) {
        this.isHovered.push(item);
      }
    },
    isHoveredOff(item) {
      if (this.isHovered.includes(item)) {
        this.isHovered = this.isHovered.filter((i) => i !== item);
      }
    },
    stopEditing(item) {
      if (
        item != "relatedProject" 
        || item != "clientRequester"
        || item != "deliveryDate"
        || item != "requestDate"
      ) {
        this.isEditing = this.isEditing.filter((i) => i !== item);
        this.isHovered = this.isHovered.filter((i) => i !== item);
      }
    },
    relaunchRelatedProject() {
      if (this.assignmentForm.relatedProject && this.assignmentForm.relatedProject.name !== "Empty") {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.relatedProject);
      }
      this.relatedProjectMenu = true;
      this.updateAssignmentData({ field: "relatedProject", data: {
        name: "Empty"
      } });
    },
    relaunchPMMenu() {
      if (this.assignmentForm.projectManager && this.assignmentForm.projectManager.id) {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.projectManager);
      }
      this.updateAssignmentData({ field: "projectManager", data: null });
      this.projectManagerMenu = true;
    },
    relaunchClientRequester() {
      if (this.assignmentForm.clientRequester && this.assignmentForm.clientRequester.user.firstName !== "Empty") {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.clientRequester);
      }
      this.clientRequesterMenu = true;
      this.updateAssignmentData({ field: "clientRequester", data: {
        user: {
          firstName: "Empty",
          lastName: "",
        }
      }})
    },
    relaunchPurchaseOrder() {
      if (this.assignmentForm.purchaseOrder) {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.purchaseOrder);
      }
      if (this.assignmentForm.relatedProject && this.assignmentForm.relatedProject.name !== "Empty") {
        this.purchaseOrderMenu = true;
        this.updateAssignmentData({ field: "purchaseOrder", data: null });
      }
    },
    relaunchFileLocation() {
      if (this.assignmentForm.fileLocation) {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.fileLocation);
      }
      this.updateAssignmentData({ field: "fileLocation", data: null });
      this.fileLocationMenu = true;
    },
    relaunchMediaType() {
      if (this.assignmentForm.mediaType) {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.mediaType);
      }
      this.updateAssignmentData({ field: "mediaType", data: null });
      this.mediaTypeMenu = true;
    },
    relaunchKeyword() {
      if (this.assignmentForm.keyword) {
        this.temporaryUserStorage = _.cloneDeep(this.assignmentForm.keyword);
      }
      this.updateAssignmentData({ field: "keyword", data: null });
      this.keywordMenu = true;
    },
    clearFileLocation() {
      this.updateAssignmentData({ field: "fileLocation", data: null });
    },
    async handleRelatedProjectUpdate(item) {
      const response = await getOneProject(item.id);
      this.updateAssignmentData({ field: 'relatedProject', data: response.data });
      this.updateAssignmentData({ field: 'clientRequester', data: {
        user: {
          firstName: "Empty",
          lastName: "",
        }
      } });
      if (this.assignmentForm.geo && this.assignmentForm.relatedProject && this.assignmentForm.relatedProject.name !== 'Empty') this.getStaffByGeo({selectedGeo: this.assignmentForm.geo});
      this.relatedProjectMenu = false;
      this.stopEditing("relatedProject");
    },
    onRelatedProjectFocus(e) {
      this.$refs.relatedProjectAutocomplete.isMenuActive = true;
    },
    onCloientRequesterFocus(e) {
      this.$refs.clientRequesterAutocomplete.isMenuActive = true;
    },
    onPurchaseOrderFocus(e) {
      this.$refs.purchaseOrderAutocomplete.isMenuActive = true;
    },
    onFileLocationFocus(e) {
      this.$refs.fileLocationAutocomplete.isMenuActive = true;
    },
    onMediaTypeFocus(e) {
      this.$refs.mediaTypeAutocomplete.isMenuActive = true;
    },
    onKeywordFocus(e) {
      this.$refs.keywordAutocomplete.isMenuActive = true;
    },
    onPMFocus(e) {
      this.$refs.pmAutocomplete.isMenuActive = true;
    },
    // setHoursSubmissionModalOpen only commits `data.assignmentInfo`, so the key
    // has to match or the dialog opens against a stale record.
    openHoursSubmission() {
      this.setHoursSubmissionModalOpen({
        status: true,
        assignmentInfo: this.hoursSubmissionInfo,
      });
    },
    // Reopening a filled dropdown clears the value first (see the relaunch*
    // helpers) so the autocomplete starts empty. If the user then clicks away
    // without picking anything the previous value has to come back — otherwise
    // just opening a dropdown and dismissing it wipes the field.
    handleClickOutside(event) {
      const dismiss = (field, menuKey, isEmpty) => {
        if (event.target.id === `editableInput-${field}`) return;
        if (!this[menuKey]) return;
        this[menuKey] = false;
        if (isEmpty(this.assignmentForm[field]) && this.temporaryUserStorage) {
          this.updateAssignmentData({
            field,
            data: _.cloneDeep(this.temporaryUserStorage),
          });
        }
        // The slot is shared by every dropdown, so it must not outlive the menu
        // that filled it.
        this.temporaryUserStorage = null;
      };

      // relatedProject / clientRequester use sentinel objects rather than null
      // for "nothing selected".
      dismiss("relatedProject", "relatedProjectMenu", (v) => v?.name === "Empty");
      dismiss(
        "clientRequester",
        "clientRequesterMenu",
        (v) => v?.user?.firstName === "Empty"
      );
      dismiss("purchaseOrder", "purchaseOrderMenu", (v) => !v);
      dismiss("projectManager", "projectManagerMenu", (v) => !v);
      dismiss("fileLocation", "fileLocationMenu", (v) => !v);
      dismiss("mediaType", "mediaTypeMenu", (v) => !v);
      dismiss("keyword", "keywordMenu", (v) => !v);

      if (event.target.id != "editableInput-priority") {
        this.priorityMenu = false;
      }
    },
    clearRelatedProject() {
      this.updateAssignmentData({ field: 'relatedProject', data: {
        name: 'Empty'
      } });
      this.updateAssignmentData({ field: 'clientRequester', data: {
        user: {
          firstName: "Empty",
          lastName: "",
        }
        } });
    },
    clearClientRequester() {
      this.updateAssignmentData({ field: 'clientRequester', data: {
        user: {
          firstName: "Empty",
          lastName: "",
        }
      }});
    },
    clearPOField() {
      this.updateAssignmentData({ field: 'purchaseOrder', data: null });
    },
    handleClientRequesterUpdate(item) {
      this.updateAssignmentData({ field: 'clientRequester', data: item });
      this.stopEditing("clientRequester");
      this.clientRequesterMenu = false;
    },
    handleClientPOUpdate(item) {
      this.updateAssignmentData({ field: "purchaseOrder", data: item });
      this.stopEditing("purchaseOrder");
      this.purchaseOrderMenu = false;
    },
    formattedPayPeriod(dateString) {
      const [year, month] = dateString.split("-");
      const date = new Date(year, month - 1, 1);

      if (isNaN(date)) {
        console.error('Invalid Date:', dateString);
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    },
    handlePMUpdate(item) {
      this.updateAssignmentData({ field: "projectManager", data: item });
      this.projectManagerMenu = false;
    },
    onPayPeriodPicker() {
      this.payPeriodMenu = false;
    },
    updateStatus(item) {
      this.updateAssignmentData({ field: "status", data: item.text });
    },
    toggleIsBilled() {
      this.updateAssignmentData({ field: "isBilled", data: !this.assignmentForm.isBilled });
    },
    getColorForInitials(initials, name) {
      if (name) {
        const colorItem = managersDesiredColors.find(el => el.name === name);
        if (colorItem) {
          return colorItem.color
        } else return initials && initials[1] && alphabetColors.find((color) => color.character === initials[1])?.color || "#9747FF";
      }
      return initials && initials[1] && alphabetColors.find((color) => color.character === initials[1])?.color || "#9747FF";
    },
    // "15d 7h" between now and an instant, without a sign — the caller supplies
    // the direction in its own wording.
    spanToNow(ts) {
      const totalHours = Math.floor(Math.abs(ts - this.nowTs) / 3600000);
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      return days ? `${days}d ${hours}h` : `${hours}h`;
    },
    relativeToNow(ts) {
      const span = this.spanToNow(ts);
      if (ts > this.nowTs) return `in ${span}`;
      if (ts < this.nowTs) return `${span} ago`;
      return 'now';
    },
    // The track is sized by the form's layout, so the anti-overlap nudge needs
    // its real width. Re-measured on resize because the card sits in a panel
    // whose width changes with the hierarchy sidebar.
    measureTimeline() {
      const track = this.$refs.timelineTrack;
      this.timelineWidth = track ? track.offsetWidth : 0;
    },
    observeTimeline() {
      this.measureTimeline();
      const track = this.$refs.timelineTrack;
      if (!track || !this.timelineResizeObserver) return;
      this.timelineResizeObserver.disconnect();
      this.timelineResizeObserver.observe(track);
    },
    // Clearing a date clears its time with it: half a deadline is not a state
    // the form, the API or the timeline has any use for.
    clearRequestDateTime() {
      this.updateAssignmentData({ field: "requestDate", data: null });
      this.updateAssignmentData({ field: "requestTime", data: null });
      this.requestDateMenu = false;
      this.isHoveredOff("editableInput-requestDate");
    },
    clearDeliveryDateTime() {
      this.updateAssignmentData({ field: "deliveryDate", data: null });
      this.updateAssignmentData({ field: "deliveryTime", data: null });
      this.deliveryDateMenu = false;
      this.isHoveredOff("editableInput-deliveryDate");
    },
    // step is 'primary' | 'secondary' | 'qc'.
    clearStepDueDateTime(step) {
      this.updateAssignmentData({
        field: "schedule",
        data: {
          ...this.assignmentForm.schedule,
          [`${step}DueDate`]: null,
          [`${step}DueTime`]: null,
        },
      });
      this[`${step}DateMenu`] = false;
      this.isHoveredOff(`editableInput-${step}DueDate`);
    },
    hasStepDueDateTime(step) {
      const schedule = this.assignmentForm.schedule || {};
      return Boolean(schedule[`${step}DueDate`] || schedule[`${step}DueTime`]);
    },
    formatDate(date) {
      const d = new Date(date)
      return d.toLocaleDateString('en-US')
    },
    // --- Work types & pricing presets -------------------------------------
    writeWorkTypes(rows) {
      this.updateAssignmentData({
        field: "pricing",
        data: { ...this.assignmentForm.pricing, workTypes: rows },
      });
    },
    selectPricingPreset(preset) {
      this.updateAssignmentData({ field: "pricingModel", data: preset });
      this.applyPricingPreset(preset);
    },
    // Replace the rows with the ones the preset prescribes. Custom prescribes
    // nothing, so switching to it keeps whatever is on screen as the starting
    // point rather than wiping the table.
    applyPricingPreset(preset) {
      const rows = buildPricingPresetRows(
        preset,
        this.assignmentForm,
        this.workTypeReferences
      );
      if (rows) this.writeWorkTypes(rows);
    },
    // Do the rows on screen already amount to this preset? Compared by name and
    // by count, so a preset carrying a row it could not resolve (a geo pair with
    // no per-word rate) still counts as unbuilt and gets its empty row drawn.
    // Names are compared as a set: a saved record may hold them in any order and
    // reordering it would mark the form dirty for nothing.
    rowsMatchPreset(expected) {
      const rows = this.assignmentForm.pricing?.workTypes ?? [];
      if (rows.length !== expected.length) return false;

      const expectedNames = [...expected].sort();
      const currentNames = rows
        .map((row) => row?.customerPriceList?.workTypeName)
        .filter(Boolean)
        .sort();
      return (
        currentNames.length === expectedNames.length &&
        currentNames.every((name, i) => name === expectedNames[i])
      );
    },
    // Settle on a preset without touching rows that already agree with it. This
    // is the path a loading assignment takes, so it must never rewrite what was
    // saved — the preset is read back out of the rows, not imposed on them.
    //
    // Deliberately ignores whatever pricingModel currently holds. Opening the
    // form blank writes "Custom" there before any record arrives, and Custom is
    // offered in every context, so honouring it here would make every loaded
    // assignment read as Custom no matter what its rows say. Between reconciles
    // the stored value is still what the dropdown shows, which is how an
    // explicit pick survives.
    reconcilePricingPreset() {
      const inferred = inferPricingPreset(this.assignmentForm);
      const preset =
        inferred && this.pricingPresetOptions.includes(inferred)
          ? inferred
          : defaultPricingPreset(this.assignmentForm);

      if (this.assignmentForm.pricingModel !== preset) {
        this.updateAssignmentData({ field: "pricingModel", data: preset });
      }

      const expected = pricingPresetWorkTypeNames(preset);
      if (!expected || this.rowsMatchPreset(expected)) return;

      this.applyPricingPreset(preset);
    },
    // The path a deliberate change of project or request type takes. The rows
    // left over belong to the old pricing regime, so the new context's default
    // wins outright rather than being inferred from them — otherwise leftovers
    // that match nothing read as "Custom" and the table sticks there.
    applyDefaultPricingPreset() {
      const preset = defaultPricingPreset(this.assignmentForm);
      this.updateAssignmentData({ field: "pricingModel", data: preset });

      const expected = pricingPresetWorkTypeNames(preset);
      if (!expected || this.rowsMatchPreset(expected)) return;

      this.applyPricingPreset(preset);
    },
    setDepartment(item) {
      this.updateAssignmentData({ field: "department", data: item });
      this.departmentMenu = false;
    },
    parentTaskOptionText(item) {
      if (!item) return "";
      if (typeof item === "string") return item;
      const id = item.cueTaskId || item.taskId || "";
      return item.taskName ? `${id} ${item.taskName}` : id;
    },
    openParentTaskPicker() {
      if (!this.canEditMainFields) return;
      this.parentTaskMenu = true;
      this.searchTasks("");
    },
    // Blur lands before the click on a menu item has become a change event, so
    // the close waits a beat — the same guard the work type picker uses.
    closeParentTaskPickerSoon() {
      setTimeout(() => {
        this.parentTaskMenu = false;
      }, 200);
    },
    clearParentTask() {
      ["taskName", "taskId", "taskDbId"].forEach((field) => {
        this.updateAssignmentData({ field, data: field === "taskName" ? "" : null });
      });
    },
    async searchTasks(search) {
      this.taskSearchLoading = true;
      try {
        const resp = await getTasks({ query: { search: search || undefined, size: 25 } });
        this.taskOptions = resp?.content || [];
      } catch (err) {
        console.error("Failed to load tasks:", err);
        this.taskOptions = [];
      }
      this.taskSearchLoading = false;
    },
    // A picked task hands its Project, Department, Client Requester and Pay
    // Period down to the assignment; free text is just a name, and detaches it
    // from whatever task it pointed at.
    async onParentTaskSelected(value) {
      if (!value) {
        this.clearParentTask();
        return;
      }
      if (typeof value === "string") {
        this.updateAssignmentData({ field: "taskName", data: value });
        this.updateAssignmentData({ field: "taskId", data: null });
        this.updateAssignmentData({ field: "taskDbId", data: null });
        this.parentTaskMenu = false;
        return;
      }
      this.updateAssignmentData({ field: "taskDbId", data: value.id ?? null });
      this.updateAssignmentData({
        field: "taskId",
        data: value.cueTaskId || value.taskId || null,
      });
      this.updateAssignmentData({ field: "taskName", data: value.taskName || "" });
      this.parentTaskMenu = false;
      await this.inheritFromTask(value.id);
    },
    // Mirrors how PMDashboardForm reads a task back: department is an enum id,
    // the project has to be expanded, and the client requester is a user id
    // resolved against that project's requester list.
    async inheritFromTask(taskDbId) {
      if (!taskDbId) return;
      const resp = await newGetTaskById(taskDbId);
      if (resp?.status !== 200 || !resp.data) return;
      const task = resp.data;

      if (task.payPeriod) {
        this.updateAssignmentData({ field: "payPeriod", data: task.payPeriod });
      }
      if (task.department != null) {
        const department =
          (this.departmentOptions || []).find((el) => el.id === task.department) ??
          task.department;
        this.updateAssignmentData({ field: "department", data: department });
      }
      if (!task.projectId) return;

      const projectResp = await getOneProject(task.projectId);
      const loaded =
        projectResp && projectResp.status >= 200 && projectResp.status < 300;
      if (!loaded || !projectResp.data) return;
      const project = projectResp.data;
      this.updateAssignmentData({ field: "relatedProject", data: project });

      if (task.clientRequesterId) {
        const requester = (project.clientRequesters || []).find(
          (el) => el.user?.id === task.clientRequesterId
        );
        if (requester) {
          this.updateAssignmentData({ field: "clientRequester", data: requester });
        }
      }
    },
    // Assigning a step to somebody else drops its acceptance: the person who
    // accepted it is no longer the person doing it, and leaving the tick behind
    // would credit the new assignee with a decision they never made. Only on a
    // real change of person, and only for the two steps that have an accept
    // flag. ADForm's syncAssigneesWithStaffList writes through the store action
    // directly rather than this setter, so swapping in the project's staff
    // objects on load does not trip it.
    setAssignee(step, value) {
      const previousId = staffUserId(this.assignmentForm[step]);
      const nextId = staffUserId(value);
      this.updateAssignmentData({ field: step, data: value });
      if (step === "qc" || previousId === nextId) return;
      this.updateAssignmentData({ field: `${step}Accepted`, data: false });
    },
    // Rows are plain objects in a store array; write a new array rather than
    // mutating in place so the change is picked up the same way every other
    // field editor in this form does it.
    replaceWorkType(target, patch) {
      const rows = (this.assignmentForm.pricing?.workTypes ?? []).map((row) =>
        row === target ? { ...row, ...patch } : row
      );
      this.writeWorkTypes(rows);
    },
    setWorkTypeQuantity(workType, value) {
      const trimmed = String(value ?? "").trim();
      this.replaceWorkType(workType, {
        quantity: trimmed === "" ? null : Number(trimmed),
      });
    },
    setWorkTypeReference(workType, reference) {
      this.replaceWorkType(workType, { customerPriceList: reference ?? null });
      this.editingWorkType = null;
    },
    removeWorkType(workType) {
      const rows = (this.assignmentForm.pricing?.workTypes ?? [])
        .filter((row) => row !== workType)
        .map((row, index) => ({ ...row, orderPosition: index + 1 }));
      this.writeWorkTypes(rows);
    },
    addWorkTypeRow() {
      const rows = this.assignmentForm.pricing?.workTypes ?? [];
      const added = {
        orderPosition: rows.length + 1,
        customerPriceList: null,
        quantity: null,
      };
      this.writeWorkTypes([...rows, added]);
      // Open its picker straight away: a blank row with nothing chosen is not a
      // useful place to leave someone.
      this.$nextTick(() => {
        this.editingWorkType = added;
      });
    },
    // Blur fires as the menu closes, which is before the click on a menu item
    // has been turned into a change event. Tearing the field out of the DOM at
    // that moment would drop the selection, so the close waits a beat.
    closeWorkTypeEditorSoon() {
      setTimeout(() => {
        this.editingWorkType = null;
      }, 200);
    },
    beginEditingWorkType(workType) {
      if (!this.canEditWorkTypes) return;
      this.editingWorkType = workType;
    },
    // Rows have no id of their own until saved; fall back to the index so a
    // freshly added blank row still gets its own vnode.
    workTypeRowKey(workType, index) {
      return workType?.customerPriceList?.id
        ? `wt-${workType.customerPriceList.id}-${index}`
        : `wt-blank-${index}`;
    },
    // Null when no row carries this rate at all, which the table renders as "-"
    // rather than a misleading $0.00.
    workTypesTotal(rateField) {
      const rows = this.sortedWorkTypes;
      const hours = this.quantityFromHours ? this.convertToDecimal() : null;
      let seen = false;
      const total = rows.reduce((sum, workType) => {
        const rate = workType.customerPriceList?.[rateField];
        if (rate == null) return sum;
        seen = true;
        const quantity = hours !== null ? hours : Number(workType.quantity) || 0;
        return sum + quantity * Number(rate);
      }, 0);
      return seen ? total : null;
    },
    formatTotal(value) {
      return value == null ? "-" : `$${Number(value).toFixed(2)}`;
    },
    async removeSubmissionFromDB() {
      if (!this.staffHours.length) {
        this.removeSubmission = false;
        return;
      }
      try {
        this.removingSubmission = true;
        const response = await removeStaffHours(this.staffHours[0].staffHoursId);
        if (response.status === 200) {
          this.handleFlash({ response: { status: 200 }, show: true });
          this.$emit("assignment-dashboard-table-refresh");
        } else {
          this.handleFlash({
            response: { data: { message: "Something went wrong", statusCode: 500 } },
            show: true,
          });
        }
      } catch (err) {
        console.error(err);
        this.handleFlash({
          response: { data: { message: "Something went wrong", statusCode: 500 } },
          show: true,
        });
      }
      this.removingSubmission = false;
      this.removeSubmission = false;
    },
    showReviewModal() {
      this.setHoursSubmissionModalOpen({ status: true, assignmentInfo: this.hoursSubmissionInfo });
      this.setStaffHoursData(this.staffHours);
      this.openReviewModal(true);
    },
    editTimeResubmission() {
      this.setHoursSubmissionModalOpen({ status: true, assignmentInfo: this.hoursSubmissionInfo });
      this.setStaffHoursData(this.staffHours);
    },
    checkPrice(workTypeCategory, workTypeName) {
      if (this.isAdmin)
        window.open(
          `${DOMAIN}/${ROUTES.customerPriceList}?WorkTypeCategory=${workTypeCategory}&Search=${workTypeName}`,
          "_blank"
        );
    },
    convertToDecimal() {
      let hours = 0;
      let minutes = 0;
      this.groupedTooltipHours.forEach(item => {
        hours += +item.hours || 0;
        minutes += +item.minutes || 0;
      })
      return hours + minutes / 60;
    },
  },
  mounted() {
    // Keeps the timeline's progress bar and "time to deliver" current without
    // making the whole tab re-render more often than once a minute.
    this.nowTimer = setInterval(() => {
      this.nowTs = officeWallClockNow();
    }, 60000);
    if (typeof ResizeObserver !== 'undefined') {
      this.timelineResizeObserver = new ResizeObserver(this.measureTimeline);
    } else {
      window.addEventListener('resize', this.measureTimeline);
    }
    this.$nextTick(this.observeTimeline);
    document.addEventListener('click', this.handleClickOutside);
    let relatedProjectInput = this.$refs.relatedProjectAutocomplete.$refs.input;
    relatedProjectInput.addEventListener('focus', this.onRelatedProjectFocus, true);
    let clientRequesterInput = this.$refs.clientRequesterAutocomplete.$refs.input;
    clientRequesterInput.addEventListener('focus', this.onCloientRequesterFocus, true);
    let purchaseOrderInput = this.$refs.purchaseOrderAutocomplete.$refs.input;
    purchaseOrderInput.addEventListener('focus', this.onPurchaseOrderFocus, true);
    let pmInput = this.$refs.pmAutocomplete.$refs.input;
    pmInput.addEventListener('focus', this.onPMFocus, true);
    let fileLocationInput = this.$refs.fileLocationAutocomplete.$refs.input;
    fileLocationInput.addEventListener('focus', this.onFileLocationFocus, true);
    let mediaTypeInput = this.$refs.mediaTypeAutocomplete.$refs.input;
    mediaTypeInput.addEventListener('focus', this.onMediaTypeFocus, true);
    let keywordInput = this.$refs.keywordAutocomplete.$refs.input;
    keywordInput.addEventListener('focus', this.onKeywordFocus, true);
  },
  beforeDestroy() {
    if (this.nowTimer) clearInterval(this.nowTimer);
    if (this.timelineResizeObserver) this.timelineResizeObserver.disconnect();
    else window.removeEventListener('resize', this.measureTimeline);
    document.removeEventListener('click', this.handleClickOutside);
    let relatedProjectInput = this.$refs.relatedProjectAutocomplete.$refs.input;
    relatedProjectInput.removeEventListener('focus', this.onRelatedProjectFocus, true);
    let clientRequesterInput = this.$refs.clientRequesterAutocomplete.$refs.input;
    clientRequesterInput.removeEventListener('focus', this.onCloientRequesterFocus, true);
    let purchaseOrderInput = this.$refs.purchaseOrderAutocomplete.$refs.input;
    purchaseOrderInput.removeEventListener('focus', this.onPurchaseOrderFocus, true);
    let pmInput = this.$refs.pmAutocomplete.$refs.input;
    pmInput.removeEventListener('focus', this.onPMFocus, true);
    let fileLocationInput = this.$refs.fileLocationAutocomplete.$refs.input;
    fileLocationInput.removeEventListener('focus', this.onFileLocationFocus, true);
    let mediaTypeInput = this.$refs.mediaTypeAutocomplete.$refs.input;
    mediaTypeInput.removeEventListener('focus', this.onMediaTypeFocus, true);
    let keywordInput = this.$refs.keywordAutocomplete.$refs.input;
    keywordInput.removeEventListener('focus', this.onKeywordFocus, true);
  }
};
</script>

<style lang="scss">
  .addFormMainTab {
    width: 100%;
    &__data {
      width: 100%;
      display: flex;
      gap: 15px;
      // Two equal columns of the 1000 content width, 15 apart -> 492.5 each.
      & > * {
        flex: 1 1 0;
        min-width: 0;
      }
      &--left {
        &--item {
          height: 40px;
          width: 100%;
          display: flex;
          align-items: center;
          // The value cell is whatever sits next to the label, and the fields
          // do not agree on what that is — .item-input, .item-input-p0, or
          // .addFormMainTab__task--content-left--text. Whichever it is, it takes
          // the rest of the column, so every value box is the same 317.5 wide.
          & > *:not(.item-container) {
            flex: 1 1 auto;
            width: auto;
            min-width: 0;
            // Stretch to the row's 40px so a hover surface inside can be told
            // to fill it — the Billed checkbox row was only as tall as the box.
            align-self: stretch;
          }
          // Several fields nest further wrappers inside that cell, and each one
          // shrink-wrapped to its text. That is why the clear cross ended up
          // butted against the value rather than at the right edge of the box
          // (it is absolutely positioned against .item-input), and why the
          // clickable area of Priority, Pay Period, Status and Department was
          // only as wide as the words in them. Every wrapper on the way down
          // fills the cell, in both directions.
          & .item-input-p0 > *,
          & .addFormMainTab__task--content-left--text,
          & .pmdashboard__task--content-left--text,
          & .user-status-name,
          & .item-input {
            width: 100%;
            min-width: 0;
            height: 100%;
          }
          // The element that actually carries the hover and the click. Full
          // width and the row's full 40px, so the whole field responds rather
          // than just the text — which is what made these feel inert.
          // PM nests its whole chain inside a .pa-0 wrapper that also carries
          // .item-input--fullfield, whose 5px horizontal padding wins and pulls
          // the chain — and so the clear cross — 5px inboard of every other
          // field's. The wrapper says pa-0; let it mean it.
          & .pa-0.bradius-4 {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          & .item-input--p,
          & .task--title,
          & .user-status-name-text {
            flex: 1 1 auto;
            min-width: 0;
            height: 100%;
            display: flex;
            align-items: center;
          }
          & .user-status-name-text {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
            line-height: 40px;
          }
          & .item-container {
            display: flex;
            align-items: center;
            // 170 label + 5 gap + 317.5 value = the 492.5 column.
            flex: 0 0 170px;
            width: 170px;
            margin-right: 5px;
            &--icon {
              width: 18px;
              height: 18px;
              color: #656F7D;
              margin-right: 10px;
              &--left {
                margin: 0  2px 0 10px;
              }
            }
            &--text {
              font-family: 'Roboto', sans-serif;
              font-weight: 400;
              font-size: 16px;
              color: #656F7D;
            }
          }
          & .item-input {
            flex: 1 1 auto;
            width: auto;
            min-width: 0;
            padding-right: 25px !important;
            position: relative;
            &--empty {
              color: #B4B4B4;
              padding: 2px 5px;
              height: 40px;
              display: flex;
              align-items: center;
              width: 100%;
            }
            &--fullfield {
              color: #000000 !important;
              padding: 2px 5px;
              font-weight: 400;
              height: 40px;
              display: flex;
              align-items: center;
              width: 100%;
            }
            &--field {
              outline: none;
              color: #000000;
              width: 100%;
            }
            &--p {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              // As a flex child (see --empty/--fullfield above), this defaults
              // to never shrinking narrower than its own text — the classic
              // flexbox min-width:auto trap — so long values (a formatted
              // date/time, say) overflow straight past the shrunk .item-input
              // box and butt up against the clear icon with no gap at all.
              // min-width: 0 lets it actually shrink and ellipsize instead;
              // margin-right keeps a breathing gap even when it doesn't need to.
              min-width: 0;
              margin-right: 8px;
            }
            &--clear {
              width:16px;
              height: 16px;
              padding: 4px;
              border-radius: 2px;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              z-index: 9000 !important;
              position: absolute !important;
              top: 50%;
              transform: translateY(-45%);
              right: 10px;
              &:hover {
                background-color: #DEDEDE;
                cursor: pointer;
              }
            }
            &--checkbox {
              width: 100%;
              height: 40px;
              margin-left: 5px;
              background-color: #ffffff;
              display: flex;
              align-items: center;
            }
          }
          & .item-input-p0 {
            padding-right: 0 !important;
            padding-left: 0 !important;
          }
        }
      }
      &--right {
        min-width: 470px;
      }
    }
    &__status {
      &--chip {
        font-family: 'Roboto', sans-serif;
        font-weight: 500;
        font-size: 16px;
        line-height: 18px;
        color: #FFFFFF;
        cursor: pointer;
      }
    }
    &__steps {
      display: flex;
      flex-direction: column;
      margin-top: 36px;
      &--header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 24px;
        &--title {
          color: #0000008A;
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          font-size: 14px;
          text-transform: uppercase;
          display: flex;
          align-items: center;
        }
        &--timeconverter {
          display: flex;
          align-items: center;
          & .steps__timeconverter--dropdown {
            display: inline-block;
            position: relative;
            width: 180px;
            border-radius: 5px;
            .dropdown-wrapper {
              display: flex;
              align-items: center;
              justify-content: space-between;
              border-radius: 5px;
              padding: 4px;
              cursor: pointer;
            }
            .dropdown-text {
              flex-grow: 1;
              font-size: 16px;
              font-weight: 400;
              color: #656F7D !important;
              font-family: 'Roboto', sans-serif !important;
            }
            .dropdown-arrow {
              background-color: transparent;
              border: none;
              cursor: pointer;
              background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-down"><polyline points="6 9 12 15 18 9"></polyline></svg>');
              background-repeat: no-repeat;
              background-position: center;
              background-size: 12px 12px;
              width: 20px;
              height: 20px;
            }
            .dropdown-menu {
              position: absolute;
              top: 100%;
              left: 0;
              right: 0;
              margin: 0;
              padding: 0;
              list-style: none;
              background-color: #fff;
              border: 1px solid #ccc;
              border-radius: 4px;
              max-height: 200px;
              min-width: 200px;
              overflow-y: auto;
              z-index: 1000;
            }
            .dropdown-menu li {
              padding: 10px;
              cursor: pointer;
            }
            .dropdown-menu li:hover {
              background-color: #f0f0f0;
            }
            .background-color {
              background-color: #D9D9D9 !important;
            }
          }
        }
      }
      &--content {
        padding: 15px 10px;
        border-radius: 6px;
        background-color: #F5F5F5;
        & .steps__fields {
          margin-top: 15px;
          display: flex;
          // Without this, the two 480px columns sit flush against each other —
          // whatever renders at the true right edge of --left (the clear cross,
          // once it's actually where it belongs and not silently overflowing)
          // butts straight into --right's icon with no breathing room at all.
          // 15px matches the row-gap already used inside each column.
          gap: 15px;
          &--left,
          &--right {
            display: flex;
            flex-direction: column;
            gap: 15px;
            // Fill the row rather than sitting at a fixed 480px. At the popup's
            // real width that left ~150px dead on the right, while the deadlines
            // table immediately below (width: 100%) ran the full width — so the
            // two blocks visibly disagreed about where the content ends. An even
            // split is what the design has anyway.
            flex: 1 1 0;
            min-width: 0;
          }
          // Inside each column the label keeps its fixed width and the field
          // next to it takes whatever is left, so the field ends flush with the
          // column instead of stopping short of it.
          .addFormMainTab__data--left--item {
            & > .item-container {
              flex: 0 0 auto;
            }
            & > .item-input,
            & > .item-input--checkbox {
              flex: 1 1 auto;
              width: auto;
              min-width: 0;
            }
          }
        }
        & .steps__table {
          margin-top: 15px;
          display: flex;
          justify-content: space-between;
          gap: 5px;
          width: 100%;
          &--left {
            width: 33%;
            &--header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              height: 40px;
              background-color: #fff;
              border-bottom: 1px solid #0000001F;
              padding: 10px 12px;
              & .header--text {
                color: #6C737A;
                font-family: 'Roboto', sans-serif;
                font-weight: 700;
                font-size: 14px;
                margin: 0;
              }
            }
            &--row {
              background-color: #fff;
              padding-left: 12px;
              // Same box as --center--row below, rather than letting the height
              // fall out of the control's own size plus a padding-bottom: 7px
              // fudge. That came out at 51px against the other columns' 50px,
              // and since every row carried the extra pixel the columns drifted
              // steadily further out of line the further down the table you got.
              height: 50px;
              margin-top: 4px;
              display: flex;
              align-items: center;
              // Vuetify puts a margin-top on .v-text-field (which v-autocomplete
              // carries); in a fixed-height flex row that only shoves the
              // control off-centre, since the row is doing the centring now.
              & .v-input {
                margin-top: 0;
              }
              // The same two lines a v-autocomplete draws — floating label over
              // value — without the control, for viewers who cannot edit.
              &--static {
                display: flex;
                flex-direction: column;
                justify-content: center;
                .static--label {
                  font-family: 'Roboto', sans-serif;
                  font-size: 12px;
                  line-height: 14px;
                  color: #00000099;
                }
                .static--value {
                  font-family: 'Roboto', sans-serif;
                  font-size: 16px;
                  line-height: 20px;
                  color: #000000DE;
                }
              }
            }
          }
          // Only the Accepted column, and only when it is showing a staff member
          // their single checkmark: the cell stretches to the height of the step
          // rows beside it so the box sits centred against them.
          &--left--single {
            display: flex;
            flex-direction: column;
            .steps__table--center--row--fill {
              flex: 1 1 auto;
              justify-content: center;
            }
          }
          &--center {
            &--row {
              background-color: #fff;
              padding-left: 12px;
              min-height: 50px;
              margin-top: 4px;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              cursor: pointer;
              // ADAcceptedSelector arrives with Vuetify's selection-control
              // spacing (margin-top: 16px + padding-top: 4px) and its own
              // margin-bottom: 8px, sized for a data-table cell. Here the row is
              // doing the centring, so that spacing only pushed the box below
              // the centre line and made this column taller than the Step and
              // Due Date columns beside it. Icon dropped to 20px to sit with the
              // rest of this table's type rather than tower over it.
              & .v-input--selection-controls {
                margin-top: 0;
                padding-top: 0;
              }
              & .v-input--checkbox .v-input__control .v-input__slot {
                margin: 0 !important;
              }
              & .v-input--checkbox .v-icon {
                font-size: 20px;
              }
              // Anchors the clear cross, which is only in the DOM while the row
              // is hovered and the deadline is actually set.
              position: relative;
              &--text {
                width: 100%;
                height: 100%;
                margin: 0;
                color: #000000DE;
                font-family: 'Roboto', sans-serif;
                font-size: 16px;
              }
              // Same cross the rest of the card's fields use.
              & .item-input--clear {
                width: 16px;
                height: 16px;
                padding: 4px;
                border-radius: 2px;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                z-index: 9000 !important;
                position: absolute !important;
                top: 50%;
                transform: translateY(-50%);
                right: 10px;
                &:hover {
                  background-color: #DEDEDE;
                  cursor: pointer;
                }
              }
            }
          }
        }
        & .steps__timeline {
          width: 100%;
          background-color: #fff;
          border-radius: 8px;
          padding: 16px;
          margin-top: 15px;
          &--header {
            color: #0072C3;
            font-family: 'Lato', 'Roboto', sans-serif;
            font-weight: 700;
            font-size: 16px;
            margin: 0 0 4px 0;
          }
          &--empty {
            font-family: 'Lato', 'Roboto', sans-serif;
            font-size: 14px;
            color: #757575;
            padding: 15px 0;
          }
          // The header colour is the fastest read on the card, so it carries the
          // state on its own before anybody looks at the bar.
          &--overdue {
            .steps__timeline--header { color: #DA1E28; }
            .timeline-track--fill { background: #DA1E28; }
          }
          &--delivered {
            .steps__timeline--header { color: #24A148; }
            .timeline-track--fill { background: #24A148; }
          }
          &--canceled {
            .steps__timeline--header { color: #8D8D8D; }
            .timeline-track--fill { background: #C6C6C6; }
          }
          &--invalid .steps__timeline--header { color: #DA1E28; }
          &--body {
            position: relative;
            padding: 4px 0 8px 0;
            .timeline-track {
              position: relative;
              width: 100%;
              height: 22px;
              background: #E0E0E0;
              border-radius: 64px;
              &--fill {
                position: absolute;
                left: 0;
                top: 0;
                height: 100%;
                max-width: 100%;
                background: #0043CE;
                border-radius: 64px;
                transition: width .3s ease;
              }
            }
            // Dots are absolutely placed at their real position on the date
            // range; `left` is the centre, so the transform is what keeps a dot
            // sitting on its date rather than to the right of it.
            .timeline-dot {
              position: absolute;
              top: 50%;
              width: 19px;
              height: 19px;
              transform: translate(-50%, -50%);
              z-index: 2;
              .circle {
                display: block;
                width: 19px;
                height: 19px;
                border-radius: 50%;
                // Dark blue by default: the deadline is still ahead of us.
                background-color: #0F62FE;
                cursor: pointer;
                transition: background-color .2s ease, box-shadow .2s ease;
              }
              &:hover {
                z-index: 4; // lift above neighbouring dots so its tooltip isn't tucked behind them
              }
              &:hover .circle {
                box-shadow: 0 0 0 3px #0F62FE33;
              }
              // Deadline has passed. Pink: this step has not been accepted yet.
              // Light blue: it has been — or, for Request Date, there was never
              // an accept step to wait on in the first place.
              &--pending .circle { background-color: #FF8F8F; }
              &--done .circle { background-color: #BCD2FF; }
              // A deadline set outside Request…Delivery is pinned to the end it
              // overshoots and ringed, so a broken schedule looks broken instead
              // of blending in as if it were a normal dot.
              &--flagged .circle {
                box-shadow: 0 0 0 2px #FFFFFF, 0 0 0 4px #DA1E28;
              }
            }
          }
        }
      }
    }
    &__workTypes {
      display: flex;
      flex-direction: column;
      margin-top: 36px;
      &--header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 24px;
        &--title {
          color: #0000008A;
          font-family: 'Roboto', sans-serif;
          font-weight: 500;
          font-size: 14px;
          text-transform: uppercase;
          display: flex;
          align-items: center;
        }
        &--pricingModel {
          .workTypes-preset-select {
            // Hugs its own label, the way the design sizes it — a fixed width
            // either clipped the longest preset or left the shortest swimming.
            flex: 0 0 auto;
            width: auto;
            // A scoped selection slot renders straight into .v-select__selections
            // (plural) — .v-select__selection is Vuetify's own default markup and
            // is not present here, which is why the gap rule was doing nothing.
            &.v-input .v-select__selections {
              flex-wrap: nowrap;
              align-items: center;
            }
            // VSelect always renders a readonly input for keyboard access. With
            // a scoped selection slot it has nothing to display, but it still
            // flex-grows — which is the empty run between the value and the
            // arrow, and what made the menu that wide too.
            &.v-input .v-select__selections input {
              flex: 0 0 0;
              width: 0;
              min-width: 0;
              padding: 0;
            }
            &--label {
              margin-right: 6px;
              white-space: nowrap;
              font-family: 'Roboto', sans-serif;
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              color: #656F7D;
            }
            &--value {
              white-space: nowrap;
              font-family: 'Roboto', sans-serif;
              font-weight: 400;
              font-size: 14px;
              line-height: 20px;
              color: #000000DE;
            }
            &.v-input .v-input__slot {
              min-height: 34px;
              padding: 0 8px 0 12px !important;
              border-radius: 4px 4px 0 0;
              background-color: #0000000F !important;
              border-bottom: 1px solid #0000006B;
              box-shadow: none !important;
            }
            // v-select reserves room for a floating label it does not have here.
            &.v-input .v-select__slot > .v-input__append-inner {
              margin-top: 0;
              align-self: center;
            }
          }
        }
      }
      &--content {
        padding: 15px 10px;
        border-radius: 6px;
        background-color: #F5F5F5;
        &--table {
          display: flex;
          flex-direction: column;
          // Grid rather than flex: whole columns come and go with the role and
          // with the hours status, and nth-child widths silently shift onto the
          // wrong column when one is removed. The track list is computed, and
          // each cell says how it aligns itself.
          .workTypes-table--header {
            display: grid;
            width: 100%;
            list-style-type: none;
            height: 48px;
            padding: 0 16px;
            background-color: #FFFFFF;
            color: #6C737A;
            font-family: 'Roboto', sans-serif;
            font-weight: 700;
            font-size: 14px;
            margin-bottom: 1px;
            &--item {
              min-width: 0;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              &.is-num { justify-content: flex-end; }
              &.is-center { justify-content: center; }
              &.is-end { justify-content: flex-end; }
            }
          }
          .workTypes-table--row {
            display: grid;
            width: 100%;
            list-style-type: none;
            height: 48px;
            padding: 0 16px;
            background-color: #FFFFFF;
            color: #000000DE;
            font-family: 'Roboto', sans-serif;
            font-weight: 400;
            font-size: 14px;
            margin-bottom: 1px;
            &--item {
              min-width: 0;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              &.is-num { justify-content: flex-end; }
              &.is-center { justify-content: center; }
              &.is-end { justify-content: flex-end; }
            }
          }
          .workTypes-table--row--total {
            font-weight: 700;
          }
          .workTypes-table--worktype-name {
            text-decoration: underline;
            cursor: pointer;
            color: #000000DE;
            &:hover { color: #1976D2; }
          }
          // An editable work type cell. The whole cell opens the picker, so it
          // needs to look like it does something — the previous version gave no
          // hover feedback at all, which left it unclear what was clickable.
          // The name inside keeps its own link behaviour and stops the click,
          // so opening the price list and changing the row stay separate.
          .workTypes-table--worktype-cell {
            cursor: pointer;
            border-radius: 4px;
            margin-left: -6px;
            padding-left: 6px;
            transition: background-color .15s ease;
            &:hover { background-color: #EDEDED; }
          }
          .workTypes-table--worktype-empty {
            color: #B4B4B4;
            text-decoration: underline;
          }
          // Inline editors. Quantity is editable under every preset; the work
          // type reference only under Custom, which is the only preset that does
          // not prescribe its own rows.
          .workTypes-table--quantity-input {
            width: 100%;
            height: 32px;
            // Flush with the column's right edge so the figure lines up under
            // its right-aligned header.
            text-align: right;
            padding: 0 0 0 8px;
            outline: none;
            color: #000000DE;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            &:hover { background-color: #F5F5F5; }
            &:focus { background-color: #F5F5F5; }
            // The spinners crowd an already narrow cell and the value is easier
            // to type than to step.
            -moz-appearance: textfield;
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
          }
          .workTypes-table--worktype-select {
            width: 95%;
            font-size: 14px;
            &.v-input .v-select__slot input { font-size: 14px; }
          }
          .v-chip.v-size--small {
            min-width: 90px;
            justify-content: center;
          }
        }
        &--actions {
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
        }
      }
    }
    .v-autocomplete .v-input__slot::before,
    .v-autocomplete .v-input__slot::after {
      border-bottom: none !important;
      content: none !important;
    }
  }

  // A field whose value comes from the parent task is drawn as its child. The
  // indent is padding, not margin, so the container keeps its shared 170px and
  // the value column stays put whether the field is nested or not.
  .item-container--nested {
    padding-left: 26px;
  }
  .item-container--arrow {
    margin-right: 6px;
    display: flex;
    align-items: center;
  }
  .parentTask-select {
    font-size: 16px;
    &.v-input .v-select__slot input { font-size: 16px; }
  }

  .bradius-4 {
    border-radius: 4px !important;
  }


  .hover {
    background-color: #F2F2F2;
  }

  .bg-2-state {
    background-color: #FFFFFF;
    cursor: pointer;
  }

  .additional-hover {
    &:hover {
      cursor: pointer;
    }
  }

  .dropdown-initials-chip {
    border-radius: 50% !important;
    height: 40px !important;
  }

  .user-status-name {
    display: flex;
    align-items: center;
    &-initials {
      min-width: 36px;
      height: 36px;
      font-size: 16px;
      font-weight: 400;
      color: #fff;
      border-radius: 50%;
      background-color: #9747FF;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &-text {
      color: #000;
      font-size: 16px !important;
      font-weight: 400 !important;
      font-family: 'Roboto', sans-serif !important;
      & a {
        color: #0000EE !important;
      }
    }
  }

  .text-white {
    color: #fff !important;
  }

  .wtMenuList {
    &__item {
      &:hover {
        cursor: pointer;
      }
    }
  }

  .custom-tooltip {
    box-shadow: 0px 0px 6px 6px #00000014;
    max-width: 267px;
  }

  // The timeline dots' tooltip (see "content-class" on their v-tooltip).
  // Top level, next to the other tooltip rules above, and not nested under
  // .addFormMainTab, because Vuetify moves tooltip content out to the app root —
  // anything scoped to an ancestor inside this component simply never matches it.
  .timeline-tooltip-content {
    text-align: center;
    padding: 4px 12px !important;
    .timeline-tooltip--label,
    .timeline-tooltip--relative {
      display: block;
      font-family: 'Lato', 'Roboto', sans-serif;
      font-weight: 700;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0.5px;
      color: #FFFFFF;
    }
    .timeline-tooltip--relative {
      margin-top: 2px;
    }
    .timeline-tooltip--flag {
      display: block;
      margin-top: 4px;
      max-width: 160px;
      white-space: normal;
      font-family: 'Lato', 'Roboto', sans-serif;
      font-size: 12px;
      line-height: 14px;
      color: #FFFFFF;
    }
  }

  .tooltip-date {
    font-family: Roboto;
    font-weight: 500;
    font-size: 14px;
    color: #000;
  }

  .tooltip-note {
    font-family: Roboto;
    font-weight: 300;
    font-size: 12px;
    color: #000;
  }

  // Rejected-publish feedback, matching the PM Dashboard task form: the label of
  // every field that blocked the save turns red and shakes once. Kept inside
  // .addFormMainTab because this stylesheet is not scoped, and given its own
  // keyframes name so the component does not depend on another file having
  // defined `shake` first.
  .addFormMainTab {
    .error-color {
      color: #BE2629 !important;
    }

    .shake {
      animation: addFormMainTab-shake 1s ease-in-out;
    }
  }

  @keyframes addFormMainTab-shake {
    0%, 100% { transform: translateY(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateY(-7px); }
    20%, 40%, 60%, 80% { transform: translateY(7px); }
  }
// v-dialog detaches its content to the app root, so these cannot be nested
// under .addFormMainTab. Same panel the assignments table uses.
.remove-submission-modal {
  background-color: #E0E0E0;
  padding: 15px;
  &__content {
    padding: 20px;
    border-radius: 4px;
    background-color: #fff;
    &--title {
      margin-bottom: 30px;
      & h6 {
        font-family: 'Roboto', sans-serif;
        font-size: 20px;
        font-weight: 500;
        line-height: 18px;
      }
    }
  }
  &__total--time {
    &-text {
      font-family: 'Roboto', sans-serif;
      font-size: 16px;
      font-weight: 600;
      line-height: 18px;
    }
    &-text2 {
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      font-weight: 300;
      line-height: 18px;
    }
    &-chip {
      padding: 2px 10px;
      border-radius: 16px;
      background-color: #E0E0E0;
      font-family: 'Roboto', sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 18px;
    }
  }
  &__action {
    margin-top: 15px;
  }
}
</style>